import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSaveStatus } from '../contexts/SaveStatusContext';
import { useSnapshotFlushOptional } from '../contexts/SnapshotFlushContext';
import { 
  saveSnapshot, 
  getSnapshot, 
  getSnapshotsBySection,
  saveSnapshotWithForwardFillMetadata,
  propagateForwardFillUpdate
} from '../supabase/snapshotService';
import { isEqual } from 'lodash';
import { getCurrentWeekKey, getWeekStartDateFromKey, getPreviousWeekKey } from '../utils/weekKeyUtils';
import { useInitialData } from '../contexts/InitialDataContext';
import { useUser } from '../contexts/UserContext';
import { logger } from '../utils/logger';
import { Cache } from '../data/cache/LocalCache';
import { validateTipTapDocument, sanitizeTipTapDocument, hasTipTapContent } from '../utils/editor/tiptapValidation';
import { rateLimiter } from '../utils/security';
import { classifyError } from '../utils/errorHandling';
import { showErrorToast, showInfoToast } from './useToast';
import type { ForwardFillDiagnosticLogInsert } from '../types/supabase-rpc';

// Feature flag: Allow forward-fill even when saved data is empty
const FORWARD_FILL_ON_EMPTY = !!import.meta.env.VITE_FORWARD_FILL_ON_EMPTY;

// Default: Auto-persist forward-filled data (can be made configurable per workspace later)
const AUTO_PERSIST_FORWARD_FILL = true;

/**
 * Diagnostic logging function (dev-only)
 * Logs forward-fill conditions to database for debugging
 * Only logs in development to prevent production database clutter
 */
async function logForwardFillDiagnostic(
  section: string,
  periodKey: string,
  conditions: {
    isAdmin: boolean;
    userId: string | null;
    hasMeaningfulData: boolean;
    isPastOrCurrentWeek: boolean;
    currentWeek: string;
    willCallRPC: boolean;
  },
  rpcResult?: { updatedRows: number; error?: string }
) {
  // Gate diagnostics to dev-only to prevent production database clutter
  if (!import.meta.env.DEV) return;
  
  try {
    const { supabase } = await import('../lib/supabaseClient');
    const logEntry: ForwardFillDiagnosticLogInsert = {
      section_key: section,
      week_key: periodKey,
      is_admin: conditions.isAdmin,
      has_user_id: !!conditions.userId,
      has_meaningful_data: conditions.hasMeaningfulData,
      is_past_or_current_week: conditions.isPastOrCurrentWeek,
      current_week_key: conditions.currentWeek,
      target_week_key: periodKey,
      will_call_rpc: conditions.willCallRPC,
      rpc_called: !!rpcResult,
      rpc_result: rpcResult?.updatedRows ?? null,
      error_message: rpcResult?.error ?? null
    };
    // Type assertion needed because Supabase generated types don't include this table
    await supabase.from('forward_fill_diagnostic_log').insert(logEntry as unknown as never);
  } catch (error) {
    // Silently fail - don't break the app if logging fails
  }
}

// TipTap validation functions moved to src/utils/editor/tiptapValidation.ts

/**
 * Helper function to check if data is meaningful (not empty/placeholder)
 * Handles section-specific logic for funnel (all zeros) and notes (TipTap docs)
 */
function hasMeaningfulData(data: unknown, section: string): boolean {
  if (data === null || typeof data === 'undefined') {
    return false;
  }
  
  // Arrays: must have at least one item
  if (Array.isArray(data)) {
    // Arrays represent tabular data; even an empty array means the user explicitly cleared rows.
    return true;
  }
  
  // Objects: check based on section type
  if (typeof data === 'object' && !Array.isArray(data)) {
    const keys = Object.keys(data);
    
    // Notes: TipTap documents - check if content has meaningful text
    if (section === 'notes') {
      // Sanitize TipTap document first if it's invalid (fix corrupted data)
      let doc = data as unknown;
      const wasInvalid = !validateTipTapDocument(doc);
      if (wasInvalid) {
        if (import.meta.env.DEV) {
          logger.warn('Invalid TipTap document structure, sanitizing', {
            section: 'notes',
            dataPreview: JSON.stringify(doc).substring(0, 200)
          }, 'hasMeaningfulData:notes');
        }
        doc = sanitizeTipTapDocument(doc);
      }
      
      // Use extracted utility function to check for meaningful content
      const hasText = hasTipTapContent(doc);
      
      if (import.meta.env.DEV) {
        logger.debug('Notes TipTap doc check', {
          hasText,
          type: (doc as Record<string, unknown>)?.type,
          contentLength: Array.isArray((doc as Record<string, unknown>)?.content) 
            ? ((doc as Record<string, unknown>).content as unknown[]).length 
            : 0,
          isValid: validateTipTapDocument(doc),
          wasSanitized: wasInvalid,
          section: 'notes',
          dataPreview: JSON.stringify(doc).substring(0, 200)
        }, 'hasMeaningfulData:notes');
      }
      
      if (hasText) {
        return true;
      }
      
      // For non-TipTap objects (legacy), check if it has any keys
      const hasKeys = keys.length > 0;
      
      if (import.meta.env.DEV) {
        logger.debug('Notes non-TipTap check', {
          hasKeys,
          keys,
          section: 'notes'
        }, 'hasMeaningfulData:notes');
      }
      
      return hasKeys;
    }
    
    // Funnel: NEVER check values - if it's an object/array, it's meaningful
    // Emptiness is determined by row existence, NOT by numeric values
    // All zeros is valid saved data - user intentionally set everything to 0
    if (section === 'funnel') {
      // Structural check only: if it's an object or array, it's meaningful
      // This ensures all-zero Funnel data is never treated as empty
      const isObjectOrArray = typeof data === 'object' && data !== null;
      return isObjectOrArray;
    }
    
    // Other objects: must have at least one key
    return keys.length > 0;
  }
  
  // Primitives: always meaningful if not null/undefined
  return true;
}

/**
 * Normalize funnel data to consistent object format
 * Handles both array format [{"id":"new-creators","value":0},...] and object format {"newCreators":5,...}
 */
function normalizeFunnelData(data: unknown): { newCreators: number; inSequence: number; inNegotiation: number; activeCreators: number } {
  const normalized = {
    newCreators: 0,
    inSequence: 0,
    inNegotiation: 0,
    activeCreators: 0
  };
  
  // Handle array format: [{"id":"new-creators","value":0},...]
  if (Array.isArray(data)) {
    data.forEach((item: unknown) => {
      if (item && typeof item === 'object' && 'id' in item && 'value' in item) {
        const value = typeof item.value === 'number' ? item.value : parseFloat(String(item.value)) || 0;
        if (item.id === 'new-creators') normalized.newCreators = value;
        else if (item.id === 'in-sequence') normalized.inSequence = value;
        else if (item.id === 'in-negotiation') normalized.inNegotiation = value;
        else if (item.id === 'active-creators') normalized.activeCreators = value;
      }
    });
  } 
  // Handle object format: {"newCreators":5,"inSequence":5,...} or mixed format
  else if (typeof data === 'object' && data !== null) {
    const dataObj = data as Record<string, unknown>;
    normalized.newCreators = typeof dataObj.newCreators === 'number' ? dataObj.newCreators : (parseFloat(String(dataObj.newCreators)) || 0);
    normalized.inSequence = typeof dataObj.inSequence === 'number' ? dataObj.inSequence : (parseFloat(String(dataObj.inSequence)) || 0);
    normalized.inNegotiation = typeof dataObj.inNegotiation === 'number' ? dataObj.inNegotiation : (parseFloat(String(dataObj.inNegotiation)) || 0);
    normalized.activeCreators = typeof dataObj.activeCreators === 'number' ? dataObj.activeCreators : (parseFloat(String(dataObj.activeCreators)) || 0);
  }
  
  return normalized;
}

interface UseSimpleSnapshotOptions<T> {
  workspaceId: string | null; // null until resolved
  section: string;            // section name (e.g., 'funnel', 'notes', 'campaignSpend')
  periodKey: string;          // ISO week key (e.g., '2025-W39')
  schema?: (data: unknown) => data is T; // optional schema for validation
  defaultValue?: T;
  enabled?: boolean;
}

interface UseSimpleSnapshotReturn<T> {
  data: T | undefined;        // simplified name
  save: (data: T) => Promise<void>; // simplified save function
  flushSave: () => Promise<void>;      // flush pending saves (async to ensure save completes)
  /** Await this before navigation: saves pending data, updates cache, no isMountedRef. Use for tab/week change. */
  flushSaveNow: () => Promise<void>;
  retryFailedSave: () => Promise<void>; // ✅ STEP 2.1: Retry last failed save
  hasFailedSave: boolean;     // ✅ STEP 2.1: Whether there's a failed save to retry
  isLoading: boolean;
  isSaving: boolean;          // new: track saving state
  rootKey: string;
  workspaceReady: boolean;
  loadError: Error | null; // ✅ RELIABILITY: Error if workspace never resolves or load times out
}

/**
 * Simple, reliable snapshot hook with proper workspace gating
 * Gates I/O until workspace is resolved, builds keys from real workspace only
 */
export function useSimpleSnapshot<T = any>({
  workspaceId,
  section,
  periodKey,
  defaultValue = undefined as T,
  enabled = true
}: UseSimpleSnapshotOptions<T>): UseSimpleSnapshotReturn<T> {
  const [state, setState] = useState<{data: T | undefined; isLoading: boolean}>({ 
    data: defaultValue, 
    isLoading: true 
  });
  const [isSaving, setIsSaving] = useState(false);
  const [initialMountComplete, setInitialMountComplete] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null); // ✅ RELIABILITY: Track load timeout errors
  const { notifySaveStart, notifySaveEnd } = useSaveStatus();
  const { initialData } = useInitialData();
  const { isAdmin, userId, viewerMode, canEditSection } = useUser();
  const snapshotFlush = useSnapshotFlushOptional(); // ✅ ARCHITECTURAL: Universal flush registration

  const workspaceReady = !!workspaceId;
  const workspaceTimeoutRef = useRef<NodeJS.Timeout | null>(null); // ✅ RELIABILITY: Track workspace resolution timeout

  const rootKey = useMemo(() => (
    workspaceReady ? `${workspaceId}:${section}_${periodKey}` : 'pending'
  ), [workspaceReady, workspaceId, section, periodKey]);

  const loadSeq = useRef(0);
  const lastCommittedRef = useRef<T | undefined>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasHydratedRef = useRef(false);
  const previousViewerModeRef = useRef(viewerMode);
  const pendingRequests = useRef<Map<string, Promise<T>>>(new Map());
  // ✅ BUG 2 FIX: Track user-initiated saves (not value equality)
  const didUserInitiateSaveRef = useRef(false);
  const pendingRpcCalls = useRef<Map<string, Promise<number>>>(new Map()); // Track pending RPC calls to prevent race conditions
  const pendingSaves = useRef<Map<string, Promise<boolean>>>(new Map()); // Track pending saves to prevent duplicate saves
  const dataRef = useRef<T | undefined>(defaultValue);
  const saveSequenceRef = useRef(0); // Track save sequence to prevent race conditions
  const isMountedRef = useRef(true); // Track if component is mounted
  const failedSaveRef = useRef<{ data: T; periodKey: string; section: string } | null>(null); // ✅ STEP 2.1: Track failed saves for retry
  const lastLoadedUpdatedAtRef = useRef<string | null>(null); // ✅ STEP 2.3: Track last loaded updated_at for conflict detection
  // ✅ CRITICAL FIX: Track latest pending save data to prevent data loss during rapid typing
  // When user types rapidly, multiple saves are queued. We need to ensure the LATEST data is saved, not intermediate states
  const pendingSaveDataRef = useRef<{ data: T; periodKey: string; section: string; sequence: number } | null>(null);
  // Unmount cleanup has empty deps [] so it closes over initial workspaceId; use ref so we always have latest
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;

  // ✅ RELIABILITY: Timeout if workspace never resolves (10-15s)
  useEffect(() => {
    if (workspaceReady) {
      // Workspace resolved - clear any pending timeout
      if (workspaceTimeoutRef.current) {
        clearTimeout(workspaceTimeoutRef.current);
        workspaceTimeoutRef.current = null;
      }
      setLoadError(null); // Clear any previous timeout error
      return;
    }

    // Workspace not ready - set timeout
    workspaceTimeoutRef.current = setTimeout(() => {
      if (!workspaceReady && isMountedRef.current) {
        const error = new Error(`Workspace failed to resolve after 12 seconds. Section: ${section}, Period: ${periodKey}`);
        setLoadError(error);
        setState(prev => ({ ...prev, isLoading: false }));
        logger.warn('Workspace resolution timeout', { section, periodKey }, `useSimpleSnapshot:${section}`);
      }
    }, 12000); // 12 second timeout

    return () => {
      if (workspaceTimeoutRef.current) {
        clearTimeout(workspaceTimeoutRef.current);
        workspaceTimeoutRef.current = null;
      }
    };
  }, [workspaceReady, section, periodKey]);

  // Cleanup: Clear pending requests on unmount to prevent memory leaks
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      pendingRequests.current.clear();
      pendingRpcCalls.current.clear();
      pendingSaves.current.clear();
      // Cancel any pending saves
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      // Cancel workspace timeout
      if (workspaceTimeoutRef.current) {
        clearTimeout(workspaceTimeoutRef.current);
        workspaceTimeoutRef.current = null;
      }
    };
  }, []);

  // Flush debounced save when rootKey changes (scope change)
  // Keep this before the loader effects so a week/workspace/section switch cannot
  // reuse a previous hydration flag and skip loading the new scope.
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      // Note: Save is lost on scope change, which is expected behavior
    }
    // Reset hydration state when rootKey changes to allow fresh load
    hasHydratedRef.current = false;
    setInitialMountComplete(false); // Reset initial mount flag
    // Don't reset loadSeq here - it will be incremented in the load effect
  }, [rootKey]);

  // ✅ CRITICAL FIX: Keep dataRef in sync with state.data
  // dataRef is the single source of truth for persistence (flush, unmount saves)
  // state is the source of truth for rendering
  // This separation prevents split-brain bugs
  useEffect(() => {
    dataRef.current = state.data;
  }, [state.data]);

  // Phase 1: Instant render from edge-injected data (T=0ms)
  useEffect(() => {
    if (!enabled || !workspaceReady || !initialData) return;
    
    // Only use edge data if week matches (prevent stale/wrong week data)
    if (initialData.week_key !== periodKey) {
      if (import.meta.env.DEV) {
        logger.debug('Edge data week mismatch', {
          edgeWeek: initialData.week_key,
          currentWeek: periodKey,
          section
        }, `useSimpleSnapshot:${section}`);
      }
      // Reset hydration flag if week doesn't match (allow forward-fill to run)
      hasHydratedRef.current = false;
      return;
    }
    
    // Map section names (edge uses shorter keys)
    const sectionKey = section === 'recommendedCreators' ? 'creators' : section;
    const edgeData = initialData[sectionKey as keyof typeof initialData];
    
    // Only hydrate from edge data if it's meaningful
    // This allows forward-fill to run for future weeks even if edge data exists
    if (edgeData && hasMeaningfulData(edgeData, section)) {
      setState({ data: edgeData as T, isLoading: false });
      dataRef.current = edgeData as T;
      lastCommittedRef.current = edgeData as T;
      hasHydratedRef.current = true;
      // Cache edge data for faster subsequent loads
      try {
        Cache.set(periodKey, section, edgeData);
      } catch (e) {
        // Ignore cache errors
      }
      
      if (import.meta.env.DEV) {
        logger.debug('Instant render from edge data (meaningful)', { section }, `useSimpleSnapshot:${section}`);
      }
    } else if (edgeData) {
      // Edge data exists but is not meaningful - don't hydrate, allow forward-fill to run
      if (import.meta.env.DEV) {
        logger.debug('Edge data exists but is not meaningful, allowing forward-fill', { section }, `useSimpleSnapshot:${section}`);
      }
      hasHydratedRef.current = false;
    }
  }, [enabled, workspaceReady, initialData, section, periodKey]);

  // Phase 2: Background SWR refresh (T=200ms, silent)
  useEffect(() => {
    if (!enabled || !workspaceReady || !hasHydratedRef.current) return;
    
    // Only run if we have edge data and current week matches
    if (!initialData || initialData.week_key !== periodKey) return;
    
    const timeoutId = setTimeout(async () => {
      try {
        const freshResult = await getSnapshot(periodKey, section, workspaceId!);
        const fresh = freshResult?.data ?? null;
        if (fresh && !isEqual(fresh, dataRef.current)) {
          setState({ data: fresh as T, isLoading: false });
          dataRef.current = fresh as T;
          lastCommittedRef.current = fresh as T;
          // Cache the refreshed data
          try {
            Cache.set(periodKey, section, fresh);
          } catch (e) {
            // Ignore cache errors
          }
          
          if (import.meta.env.DEV) {
            logger.debug('Background refresh updated', { section }, `useSimpleSnapshot:${section}`);
          }
        }
      } catch (error) {
        // Keep edge data on error (graceful degradation)
        if (import.meta.env.DEV) {
          logger.warn('Background refresh failed', { error, section }, `useSimpleSnapshot:${section}`);
        }
      }
    }, 200);
    
    return () => clearTimeout(timeoutId);
  }, [enabled, workspaceReady, initialData, periodKey, section, workspaceId]);

  // Load data from Supabase when workspace is ready
  useEffect(() => {
    if (!enabled) {
      // If disabled, set default and skip loading
      setState({ data: defaultValue, isLoading: false });
      return;
    }
    
    // ✅ Guard against missing required params
    if (!workspaceId || !periodKey || !section) {
      setState(prev => ({ ...prev, isLoading: false }));
      if (import.meta.env.DEV) {
        logger.warn('Missing required params, skipping load', { workspaceId, periodKey, section }, 'useSimpleSnapshot');
      }
      return;
    }
    
    // ✅ FIX: Cancel pending requests for different periodKeys (week switches)
    // This prevents stale data from loading when switching weeks quickly
    const currentRequestKey = `${workspaceId}:${section}:${periodKey}`;
    pendingRequests.current.forEach((_promise, key) => {
      if (key !== currentRequestKey) {
        // Cancel old requests for different weeks
        pendingRequests.current.delete(key);
        if (import.meta.env.DEV) {
          logger.debug('Cancelled stale request', { cancelledKey: key, currentKey: currentRequestKey }, `useSimpleSnapshot:${section}`);
        }
      }
    });
    
    // Check if target week is in the future - if so, always attempt forward-fill
    // even if edge data exists (edge data is only for current week)
    const targetDate = getWeekStartDateFromKey(periodKey);
    const currentDate = getWeekStartDateFromKey(getCurrentWeekKey());
    const isFutureWeek = targetDate && currentDate && targetDate.getTime() > currentDate.getTime();
    
    // Skip if already hydrated from meaningful edge data AND not a future week
    // For future weeks, always attempt forward-fill even if edge data exists
    if (hasHydratedRef.current && !isFutureWeek) {
      if (import.meta.env.DEV) {
        logger.debug('Skipping main load - already hydrated from edge data', { section, periodKey }, `useSimpleSnapshot:${section}`);
      }
      return;
    }

    if (!workspaceReady) { 
      setState(s => ({...s, isLoading: false})); // Don't show loading for unready workspace
      return; 
    }

    // FAST PATH: Check localStorage cache first for instant render on subsequent visits
    // This makes returning users see content immediately while fresh data loads in background
    try {
      const cached = Cache.get(periodKey, section);
      
      // 🔍 PRODUCTION DEBUG: Log cache check (ALL sections)
      console.log(`[useSimpleSnapshot] ${section} cache check`, {
        periodKey,
        section,
        hasCachedValue: cached.value !== undefined,
        cachedValuePreview: cached.value ? JSON.stringify(cached.value).substring(0, 100) : 'undefined',
        isFresh: cached.fresh,
        timestamp: new Date().toISOString()
      });
      
      if (cached.value !== undefined) {
        // Show cached data immediately (even if not "fresh")
        console.log(`[useSimpleSnapshot] ${section} using cached data`);
        setState({ data: cached.value as T, isLoading: !cached.fresh });
        dataRef.current = cached.value as T;
        lastCommittedRef.current = cached.value as T;
        
        if (import.meta.env.DEV) {
          logger.debug('Instant render from localStorage cache', { 
            section, 
            periodKey, 
            fresh: cached.fresh 
          }, `useSimpleSnapshot:${section}`);
        }
        
        // ✅ OPTIMIZATION: If cache is fresh, skip API call entirely for faster loading
        if (cached.fresh) {
          hasHydratedRef.current = true;
          setInitialMountComplete(true); // Mark as complete immediately since we have fresh cached data
          return; // Skip API call - we have fresh cached data
        }
        
        // ✅ QUICK WIN 3: Skip background fetch when:
        // - Week is NOT current week (past/future weeks don't change)
        // - User is NOT in admin/edit mode (viewers don't need fresh data)
        // - Cached data is fresh (within 5 minutes)
        const currentWeekKey = getCurrentWeekKey();
        const isNotCurrentWeek = periodKey !== currentWeekKey;
        // ✅ FIX 3: Use canEditSection instead of isAdmin for edit mode check
        const isNotEditMode = !canEditSection(section); // Cannot edit this section
        const shouldSkipBackgroundFetch = isNotCurrentWeek && isNotEditMode && cached.fresh;
        
        if (shouldSkipBackgroundFetch) {
          // Skip background fetch - cached data is fresh and user can't edit
          hasHydratedRef.current = true;
          setInitialMountComplete(true);
          if (import.meta.env.DEV) {
            logger.debug('Skipping background fetch (fresh cache, not current week, not edit mode)', {
              section,
              periodKey,
              currentWeekKey,
              isAdmin,
              viewerMode
            }, `useSimpleSnapshot:${section}`);
          }
          return; // Skip API call
        }
        
        // ✅ OPTIMIZATION: Continue to fetch in background, but don't block UI
        // The cached data is already displayed, fresh data will update when ready
        
        // ✅ CRITICAL FIX: If past week and cache shows empty data, don't fetch (prevent forward-fill)
        // This prevents data resurrection in past weeks after 3+ minutes
        // When user clears W39 (9/22/25), stale cache shows empty, but DB fetch might forward-fill from W38
        // BUT: If week was deleted OR row doesn't exist in DB, allow forward-fill even if cache is empty
        const targetDateCache = getWeekStartDateFromKey(periodKey);
        const currentDateCache = getWeekStartDateFromKey(getCurrentWeekKey());
        const isPastWeekCache = targetDateCache && currentDateCache && targetDateCache.getTime() < currentDateCache.getTime();
        
        // ✅ CRITICAL FIX: Check if week was deleted - if so, allow forward-fill even if cache is empty
        let isDeletedWeekCache = false;
        try {
          const deletedWeeks = JSON.parse(sessionStorage.getItem('marlo:deletedWeeks') || '[]') as string[];
          isDeletedWeekCache = deletedWeeks.includes(periodKey);
        } catch (e) {
          // Non-critical
        }
        
        // ✅ CRITICAL FIX: If week is NOT in deleted list AND cache is empty, clear cache to allow forward-fill
        // The forward-fill logic (which is async) will check if row exists in DB
        // This handles the case where user cleared sessionStorage but week still has no DB rows
        // We clear the cache here so forward-fill can run, and forward-fill will check row existence
        if (isPastWeekCache && cached.value !== undefined && !isDeletedWeekCache) {
          const isEmptyCache = Array.isArray(cached.value) && cached.value.length === 0 ||
                          (section === 'funnel' && typeof cached.value === 'object' &&
                           (cached.value as any).newCreators === 0 && 
                           (cached.value as any).inSequence === 0 &&
                           (cached.value as any).inNegotiation === 0 &&
                           (cached.value as any).activeCreators === 0);
          
          // If cache is empty, clear it to allow forward-fill to check DB
          // Forward-fill logic will handle checking if row exists
          if (isEmptyCache) {
            try {
              Cache.clear(periodKey, section);
              if (import.meta.env.DEV) {
                logger.debug('Cleared empty cache to allow forward-fill check', {
                  section,
                  periodKey,
                  isDeletedWeek: false
                }, `useSimpleSnapshot:${section}`);
              }
              // Don't return - continue to DB fetch and forward-fill
            } catch (e) {
              // Non-critical
            }
          }
        }
        
        if (isPastWeekCache && cached.value !== undefined && !isDeletedWeekCache) {
          // Only block forward-fill if:
          // 1. Past week
          // 2. Cache exists
          // 3. Week NOT deleted
          // 4. Row EXISTS in DB (if row doesn't exist, allow forward-fill)
          const isEmptyCache = Array.isArray(cached.value) && cached.value.length === 0 ||
                          (section === 'funnel' && typeof cached.value === 'object' &&
                           (cached.value as any).newCreators === 0 && 
                           (cached.value as any).inSequence === 0 &&
                           (cached.value as any).inNegotiation === 0 &&
                           (cached.value as any).activeCreators === 0);
          
          if (isEmptyCache) {
            // User explicitly cleared this past week - don't forward-fill
            // Use cached empty data and skip DB fetch to prevent forward-fill from previous weeks
            // BUT: If week was deleted OR row doesn't exist, allow forward-fill (handled by checks above)
            setState({ data: cached.value as T, isLoading: false });
            hasHydratedRef.current = true;
            if (import.meta.env.DEV) {
              logger.debug('Using cached empty data for past week (prevent forward-fill)', {
                section,
                periodKey,
                targetDate: targetDateCache?.toISOString(),
                currentDate: currentDateCache?.toISOString(),
                isDeletedWeek: false,
                rowExists: true
              }, `useSimpleSnapshot:${section}`);
            }
            return; // Skip DB fetch and forward-fill
          }
        } else if (isPastWeekCache && cached.value !== undefined && isDeletedWeekCache) {
          // ✅ CRITICAL FIX: Week was deleted - clear cache and allow forward-fill
          // This ensures deleted weeks forward-fill even if cache shows empty
          try {
            Cache.clear(periodKey, section);
            if (import.meta.env.DEV) {
              logger.debug('Cleared cache to allow forward-fill', {
                section,
                periodKey,
                targetDate: targetDateCache?.toISOString(),
                currentDate: currentDateCache?.toISOString(),
                isDeletedWeek: isDeletedWeekCache
              }, `useSimpleSnapshot:${section}`);
            }
          } catch (e) {
            // Non-critical
          }
          // Don't return - continue to DB fetch and forward-fill
        }
        // If cache exists but not fresh, continue to fetch fresh data in background
      }
    } catch (error) {
      // localStorage might be unavailable, continue with normal flow
      if (import.meta.env.DEV) {
        logger.debug('localStorage cache check failed', { error }, `useSimpleSnapshot:${section}`);
      }
    }

    // Don't wipe existing state when starting load (preserves cache/previous data on load failure)
    setState(prev => ({ data: prev.data ?? defaultValue, isLoading: true }));
    // Do NOT mutate lastCommittedRef here — only on successful load or save (keeps "confirmed persisted" invariant)
    hasHydratedRef.current = false;


    const seq = ++loadSeq.current;
    const isInitialMount = !initialMountComplete;
    
    (async () => {
      try {
        
        // Check for pending request to prevent duplicates
        const requestKey = `${workspaceId}:${section}:${periodKey}`;
        let dataPromise = pendingRequests.current.get(requestKey) as Promise<{ data: T; rowExists: boolean } | null> | undefined;
        
        if (!dataPromise) {
          dataPromise = getSnapshot(periodKey, section, workspaceId) as Promise<{ data: T; rowExists: boolean } | null>;
          pendingRequests.current.set(requestKey, dataPromise as any);
        }
        
        const result = await dataPromise;
        
        // 🔍 PRODUCTION DEBUG: Log what we got from DB (ALL sections)
        console.log(`[useSimpleSnapshot] ${section} getSnapshot result`, {
          periodKey,
          section,
          resultIsNull: result === null,
          resultPreview: result ? JSON.stringify(result).substring(0, 100) : 'null',
          timestamp: new Date().toISOString()
        });
        
        // When getSnapshot returns null (load failed: timeout/network/error), keep existing state — do NOT overwrite with defaultValue
        if (result === null) {
          console.log(`[useSimpleSnapshot] ${section} getSnapshot returned null - keeping existing state`);
          setState(prev => ({ ...prev, isLoading: false }));
          pendingRequests.current.delete(requestKey);
          return;
        }
        const data: T | null = (result?.data ?? null) as T | null;
        const snapshotRowExists = result?.rowExists ?? false;
        
        // ✅ FIX: Check if this request is still valid (periodKey might have changed)
        // Only process if this is still the current request for this periodKey
        const currentRequestKey = `${workspaceId}:${section}:${periodKey}`;
        if (requestKey !== currentRequestKey) {
          // PeriodKey changed, ignore this stale response
          pendingRequests.current.delete(requestKey);
          if (import.meta.env.DEV) {
            logger.debug('Ignoring stale response (periodKey changed)', { 
              staleKey: requestKey, 
              currentKey: currentRequestKey 
            }, `useSimpleSnapshot:${section}`);
          }
          return;
        }
        
        pendingRequests.current.delete(requestKey);
        
        // Strict stale guard - only apply if this is still the current request
        // But allow initial mount requests to complete even if sequence changed
        if (loadSeq.current !== seq && !isInitialMount) {
          return;
        }
        
        // Mark initial mount as complete
        if (isInitialMount) {
          setInitialMountComplete(true);
        }
        
        // ✅ STEP 2.3: Fetch updated_at timestamp for conflict detection
        let loadedUpdatedAt: string | null = null;
        try {
          const { supabase: supabaseClient } = await import('../lib/supabaseClient');
          const { data: recordData } = await supabaseClient
            .from('snapshots')
            .select('updated_at')
            .eq('workspace_id', workspaceId!)
            .eq('week_key', periodKey)
            .eq('section_key', section)
            .maybeSingle();
          
          if (recordData && typeof recordData === 'object' && 'updated_at' in recordData) {
            const record = recordData as { updated_at: string };
            loadedUpdatedAt = record.updated_at;
            lastLoadedUpdatedAtRef.current = loadedUpdatedAt;
          }
        } catch (error) {
          // Ignore errors - conflict detection is best-effort
          if (import.meta.env.DEV) {
            logger.debug('Failed to fetch updated_at for conflict detection', { error }, `useSimpleSnapshot:${section}`);
          }
        }
        
        // ✅ FIX: snapshotRowExists is now correctly set from getSnapshot result
        // This distinguishes between "row doesn't exist" vs "row exists with null/empty data"
        
        // ✅ PHASE 3: Comprehensive load path logging
        // Check cache first
        const cacheKey = `dash:${periodKey}:${section}`;
        const cacheResult = Cache.get<T>(periodKey, section);
        const cacheHit = cacheResult.value !== undefined;
        
        // Determine data_status
        let dataStatus: string;
        if (data === null || typeof data === 'undefined') {
          dataStatus = 'NULL';
        } else if (Array.isArray(data) && data.length === 0) {
          dataStatus = 'EMPTY_ARRAY';
        } else if (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0) {
          dataStatus = 'EMPTY_OBJECT';
        } else if (section === 'notes' && !hasTipTapContent(data)) {
          dataStatus = 'TIPTAP_EMPTY_DOC';
        } else {
          // ✅ BUG 1 FIX: Funnel data is always HAS_DATA if it's an object (including all zeros)
          // No value-based checks - structure determines status
          dataStatus = 'HAS_DATA';
        }
        
        if (import.meta.env.DEV) {
          const targetDate = getWeekStartDateFromKey(periodKey);
          const currentDate = getWeekStartDateFromKey(getCurrentWeekKey());
          const isPastWeek = targetDate && currentDate && targetDate.getTime() < currentDate.getTime();
          
          logger.debug('[Phase3:Load] Section load start', {
            section,
            targetWeek: periodKey,
            snapshotRowExists,
            dataStatus,
            cacheHit,
            cacheKey,
            hasData: data !== null && typeof data !== 'undefined',
            dataType: Array.isArray(data) ? 'array' : typeof data,
            dataLength: Array.isArray(data) ? data.length : 'N/A',
            meaningfulData: hasMeaningfulData(data, section),
            isPastWeek
          }, `useSimpleSnapshot:${section}`);
        }
        
        if (snapshotRowExists) {
          // Check if this is a past week (data should not be forward-filled if past and empty)
          const targetDateCheck = getWeekStartDateFromKey(periodKey);
          const currentDateCheck = getWeekStartDateFromKey(getCurrentWeekKey());
          const isPastWeek = targetDateCheck && currentDateCheck && targetDateCheck.getTime() < currentDateCheck.getTime();
          
          // ✅ FIX: If the existing snapshot is an empty array, check if it's intentional
          // For PAST weeks: empty array might be from failed delete (rows left behind) - allow forward-fill
          // For FUTURE weeks: empty array might be intentional for linear sections, but cumulative sections can forward-fill
          if (Array.isArray(data) && data.length === 0) {
            const isLinearSection = section === 'funnel' || section === 'recommendedCreators' || section === 'notes';
            
            // ✅ FIX: Past weeks with empty arrays - allow forward-fill (handles case where delete left empty rows)
            // Fall through to forward-fill logic below instead of returning early
            if (isPastWeek) {
              if (import.meta.env.DEV) {
                logger.debug('Past week has empty array - allowing forward-fill (may be from failed delete)', {
                  section,
                  targetWeek: periodKey,
                  isPastWeek: true
                }, `useSimpleSnapshot:${section}`);
              }
              // Don't return - fall through to forward-fill logic at end of CASE A
            }
            
            // Future weeks: linear sections treat empty as intentional, cumulative sections can forward-fill
            if (isLinearSection) {
              setState({ data: data as any, isLoading: false });
              lastCommittedRef.current = data as any;
              hasHydratedRef.current = true;
              try { 
                Cache.set(periodKey, section, data); 
              } catch (error) {
                if (import.meta.env.DEV) {
                  logger.debug('Cache operation failed (non-critical)', { error, section, periodKey }, `useSimpleSnapshot:${section}`);
                }
              }
              return;
            }
            
            // For cumulative sections in future weeks, fall through to forward-fill logic
            // (Don't return here - allow forward-fill to proceed)
          }

          // ✅ FIX: If empty AND forward-fill-on-empty is disabled → check if we should still forward-fill
          // For PAST weeks with empty data: allow forward-fill (handles case where delete left empty rows)
          // Only block if data is truly meaningful (not empty)
          // ✅ BUG 1 FIX: Funnel emptiness is determined ONLY by row existence, NOT by values
          // If a snapshot row exists, it's never empty - regardless of numeric values (including all zeros)
          // This ensures all-zero Funnel data is never forward-filled or overwritten
          const isEmptyCheck = Array.isArray(data) && data.length === 0;
          
          // ✅ CRITICAL FIX: If row exists, ALWAYS load it directly (never forward-fill or re-forward-fill)
          // This applies to ALL sections to prevent forward-fill from overwriting saved data
          // Row existence = user explicitly saved this week (even if empty)
          // The "re-forward-fill" logic below was overwriting saved data for future weeks
          if (snapshotRowExists) {
            // Row exists - load it directly, even if empty/meaningless
            // This respects user intent: if they saved it, they want it
            console.log(`[useSimpleSnapshot] ${section} row exists - loading directly (not forward-filling)`, {
              section,
              targetWeek: periodKey,
              isPastWeek,
              rowExists: snapshotRowExists,
              dataStatus,
              dataPreview: JSON.stringify(data).substring(0, 200)
            });
            
            if (import.meta.env.DEV) {
              logger.debug(`${section} row exists - loading directly (not forward-filling)`, {
                section,
                targetWeek: periodKey,
                isPastWeek,
                rowExists: snapshotRowExists,
                data: JSON.stringify(data).substring(0, 200)
              }, `useSimpleSnapshot:${section}`);
            }
            setState({ data: (data ?? undefined) as T, isLoading: false });
            lastCommittedRef.current = data as any;
            hasHydratedRef.current = true;
            try { 
              Cache.set(periodKey, section, data); 
            } catch (error) {
              if (import.meta.env.DEV) {
                logger.debug('Cache operation failed (non-critical)', { error, section, periodKey }, `useSimpleSnapshot:${section}`);
              }
            }
            return;
          }
          
          if (isEmptyCheck && !FORWARD_FILL_ON_EMPTY) {
            // ✅ PERFORMANCE: Single getSnapshotsBySection instead of up to 12 sequential getSnapshot calls
            if (section === 'liveAssets' || section === 'campaignSpend' || section === 'promoCodes') {
              const sectionSnapshots = await getSnapshotsBySection(section, periodKey, 12);
              const targetDateEmpty = getWeekStartDateFromKey(periodKey);
              let sourceWeek: string | null = null;
              let sourceData: unknown = null;
              if (sectionSnapshots?.length && targetDateEmpty) {
                for (const s of sectionSnapshots) {
                  const sDate = getWeekStartDateFromKey(s.week_key);
                  if (!sDate || sDate.getTime() >= targetDateEmpty.getTime()) continue;
                  const meaningful = hasMeaningfulData(s.data, section);
                  if (meaningful && (!sourceWeek || (getWeekStartDateFromKey(sourceWeek) && sDate.getTime() > getWeekStartDateFromKey(sourceWeek)!.getTime()))) {
                    sourceWeek = s.week_key;
                    sourceData = s.data;
                  }
                }
              }
              if (sourceWeek && sourceData !== null && sourceData !== undefined) {
                let clonedData = JSON.parse(JSON.stringify(sourceData));
                if ((section === 'campaignSpend' || section === 'promoCodes') && Array.isArray(clonedData)) {
                  const beforeCount = clonedData.length;
                  clonedData = clonedData.filter((item: Record<string, unknown>) => !item.deleted);
                  if (beforeCount !== clonedData.length && import.meta.env.DEV) {
                    logger.debug('Filtered deleted items during forward-fill (from empty snapshot)', {
                      before: beforeCount,
                      after: clonedData.length,
                      sourceWeek,
                      targetWeek: periodKey
                    }, `useSimpleSnapshot:${section}`);
                  }
                }
                if (import.meta.env.DEV) {
                  const itemCount = Array.isArray(clonedData) ? clonedData.length : 'data';
                  logger.debug('Forward-fill from empty snapshot (batch getSnapshotsBySection)', {
                    section,
                    sourceWeek,
                    targetWeek: periodKey,
                    itemCount
                  }, `useSimpleSnapshot:${section}`);
                }
                setState({ data: clonedData, isLoading: false });
                lastCommittedRef.current = clonedData;
                hasHydratedRef.current = true;
                try {
                  Cache.set(periodKey, section, clonedData);
                } catch (e) {
                  // Ignore cache errors
                }
                if (AUTO_PERSIST_FORWARD_FILL && userId) {
                  await saveSnapshotWithForwardFillMetadata(periodKey, section, clonedData, sourceWeek, userId, workspaceId!);
                }
                return;
              }
            }
            
            // No source found, treat empty as intentional
            if (import.meta.env.DEV) {
              logger.debug('Found empty snapshot, treating as intentional (no forward-fill source)', {
                section,
                periodKey
              }, `useSimpleSnapshot:${section}`);
            }
            setState({ data: data, isLoading: false });
            lastCommittedRef.current = data;
            hasHydratedRef.current = true;
            // Cache the fetched data for faster subsequent loads
            try {
              Cache.set(periodKey, section, data);
            } catch (e) {
              // Ignore cache errors
            }
            return;
          }
          
          // CASE: Existing data exists - check if we should re-forward-fill from previous week
          // ✅ PERFORMANCE: Single getSnapshotsBySection instead of up to 12 sequential getSnapshot calls
          if (section === 'liveAssets' || section === 'campaignSpend' || section === 'promoCodes') {
            const targetDateReFill = getWeekStartDateFromKey(periodKey);
            const currentDateReFill = getWeekStartDateFromKey(getCurrentWeekKey());
            const isFutureWeek = targetDateReFill && currentDateReFill && targetDateReFill.getTime() > currentDateReFill.getTime();
            const currentDataIsEmpty =
              (Array.isArray(data) && data.length === 0) ||
              (!Array.isArray(data) && !hasMeaningfulData(data, section));
            const sectionSnapshotsRefill = await getSnapshotsBySection(section, periodKey, 12);
            let sourceWeekRefill: string | null = null;
            let sourceDataRefill: unknown = null;
            if (sectionSnapshotsRefill?.length && targetDateReFill) {
              for (const s of sectionSnapshotsRefill) {
                const sDate = getWeekStartDateFromKey(s.week_key);
                if (!sDate || sDate.getTime() >= targetDateReFill.getTime()) continue;
                const meaningful = hasMeaningfulData(s.data, section);
                if (meaningful && (!sourceWeekRefill || (getWeekStartDateFromKey(sourceWeekRefill) && sDate.getTime() > getWeekStartDateFromKey(sourceWeekRefill)!.getTime()))) {
                  sourceWeekRefill = s.week_key;
                  sourceDataRefill = s.data;
                }
              }
            }
            if (sourceWeekRefill && sourceDataRefill !== null && sourceDataRefill !== undefined) {
              const shouldReForwardFill = isFutureWeek || currentDataIsEmpty;
              if (shouldReForwardFill) {
                let cloned = JSON.parse(JSON.stringify(sourceDataRefill));
                if ((section === 'campaignSpend' || section === 'promoCodes') && Array.isArray(cloned)) {
                  cloned = cloned.filter((item: Record<string, unknown>) => !item.deleted);
                }
                if (import.meta.env.DEV) {
                  logger.debug('Re-forward-filling from previous week (batch getSnapshotsBySection)', {
                    section,
                    sourceWeek: sourceWeekRefill,
                    targetWeek: periodKey,
                    currentDataIsEmpty,
                    isFutureWeek
                  }, `useSimpleSnapshot:${section}`);
                } else {
                  logger.info('[Forward-Fill] Re-forward-filling from previous week', {
                    section,
                    sourceWeek: sourceWeekRefill,
                    targetWeek: periodKey
                  });
                }
                setState({ data: cloned, isLoading: false });
                lastCommittedRef.current = cloned;
                hasHydratedRef.current = true;
                try {
                  Cache.set(periodKey, section, cloned);
                } catch (error) {
                  if (import.meta.env.DEV) {
                    logger.debug('Cache operation failed (non-critical)', { error, section, periodKey }, `useSimpleSnapshot:${section}`);
                  }
                }
                if (AUTO_PERSIST_FORWARD_FILL && userId) {
                  await saveSnapshotWithForwardFillMetadata(periodKey, section, cloned, sourceWeekRefill, userId, workspaceId!);
                }
                return;
              }
            }
          }
          
          // For future weeks, check if there's a MORE RECENT week with data (linear sections only)
          // CRITICAL FIX: Only re-forward-fill for FUTURE weeks, not past weeks
          // Past weeks should NEVER be overwritten by re-forward-fill
          // This prevents cascading overwrites that destroy historical data
          const targetDateReFill = getWeekStartDateFromKey(periodKey);
          const currentDateReFill = getWeekStartDateFromKey(getCurrentWeekKey());
          const isFutureWeek = targetDateReFill && currentDateReFill && targetDateReFill.getTime() > currentDateReFill.getTime();
          
          // Only check for re-forward-fill from more recent weeks if this is a FUTURE week
          // Past weeks should preserve their original data (no re-forward-fill from more recent weeks)
          if (isFutureWeek && targetDateReFill) {
            // For linear sections, check if there's a more recent week with meaningful data
            if (section === 'funnel' || section === 'recommendedCreators' || section === 'notes') {
              // For linear sections, check if there's a more recent week with meaningful data
              const sectionSnapshots = await getSnapshotsBySection(section, periodKey, 20);
              
              if (loadSeq.current !== seq) return; // stale check
              
              if (sectionSnapshots && sectionSnapshots.length > 0) {
                // Find the most recent week STRICTLY BEFORE target with meaningful data
                let mostRecentWeek: string | null = null;
                let mostRecentData: unknown = null;
                let mostRecentWeekFallback: string | null = null;
                let mostRecentDataFallback: unknown = null;
                
                if (!targetDate) {
                  // Fall through to default if targetDate is invalid
                  setState({ data: defaultValue, isLoading: false });
                  lastCommittedRef.current = defaultValue;
                  hasHydratedRef.current = true;
                  return;
                }
                
                for (const snapshot of sectionSnapshots) {
                  const snapshotDate = getWeekStartDateFromKey(snapshot.week_key);
                  if (!snapshotDate || snapshotDate.getTime() >= targetDate.getTime()) {
                    continue; // Skip same week or future weeks
                  }
                  
                  const snapshotHasMeaningfulData = hasMeaningfulData(snapshot.data, section);
                  
                  // Track the most recent week with ANY data (fallback)
                  if (snapshot.data !== null && snapshot.data !== undefined) {
                    if (!mostRecentWeekFallback || snapshotDate.getTime() > getWeekStartDateFromKey(mostRecentWeekFallback)!.getTime()) {
                      mostRecentWeekFallback = snapshot.week_key;
                      mostRecentDataFallback = snapshot.data;
                    }
                  }
                  
                  // Prefer meaningful data over fallback
                  if (snapshotHasMeaningfulData) {
                    if (!mostRecentWeek || snapshotDate.getTime() > getWeekStartDateFromKey(mostRecentWeek)!.getTime()) {
                      mostRecentWeek = snapshot.week_key;
                      mostRecentData = snapshot.data;
                    }
                  }
                }
                
                // Use meaningful data if found, otherwise use fallback
                if (!mostRecentWeek && mostRecentWeekFallback) {
                  mostRecentWeek = mostRecentWeekFallback;
                  mostRecentData = mostRecentDataFallback;
                }
                
                // If we found a more recent week with data, re-forward-fill from it
                // This ensures that if W41 has newer data, W42 will show W41's data
                if (mostRecentWeek && mostRecentData !== null && typeof mostRecentData !== 'undefined') {
                  const mostRecentDate = getWeekStartDateFromKey(mostRecentWeek);
                  
                  // Always re-forward-fill if we found a more recent week with data
                  if (mostRecentDate && targetDate && mostRecentDate.getTime() < targetDate.getTime()) {
                    // Deep clone to prevent reference sharing
                    let clonedData = JSON.parse(JSON.stringify(mostRecentData));
                    
                    // Normalize funnel data format
                    if (section === 'funnel') {
                      clonedData = normalizeFunnelData(clonedData);
                    }
                    
                    // Sanitize TipTap documents
                    if (section === 'notes' && !validateTipTapDocument(clonedData)) {
                      clonedData = sanitizeTipTapDocument(clonedData);
                    }
                  
                  if (import.meta.env.DEV) {
                    logger.debug('Re-forward-filling from more recent week', {
                      section,
                      existingWeek: periodKey,
                      sourceWeek: mostRecentWeek,
                      targetWeek: periodKey,
                      reason: 'More recent week with data found, re-forward-filling'
                    }, `useSimpleSnapshot:${section}`);
                  } else {
                    logger.info('[Forward-Fill] Re-forward-filling from more recent week', {
                      section,
                      existingWeek: periodKey,
                      sourceWeek: mostRecentWeek,
                      targetWeek: periodKey
                    });
                  }
                  
                  setState({ data: clonedData, isLoading: false });
                  lastCommittedRef.current = clonedData;
                  hasHydratedRef.current = true;
                  // Cache re-forward-filled data
                  try {
                    Cache.set(periodKey, section, clonedData);
                  } catch (e) {
                    // Ignore cache errors
                  }
                  
                  // Auto-persist the re-forward-filled data
                  if (AUTO_PERSIST_FORWARD_FILL) {
                    try {
                      // CRITICAL: Only save if we actually found source data AND it's meaningful
                      if (mostRecentWeek && mostRecentData !== null && typeof mostRecentData !== 'undefined') {
                        // Double-check the data is meaningful before persisting
                        if (hasMeaningfulData(clonedData, section)) {
                          if (userId && mostRecentWeek) {
                            const saveSuccess = await saveSnapshotWithForwardFillMetadata(
                              periodKey,
                              section,
                              clonedData,
                              mostRecentWeek,
                              userId,
                              workspaceId!
                            );
                            if (saveSuccess) {
                              if (import.meta.env.DEV) {
                                logger.debug('Re-forward-fill auto-persisted with metadata', {
                                  section,
                                  sourceWeek: mostRecentWeek,
                                  targetWeek: periodKey
                                }, `useSimpleSnapshot:${section}`);
                              } else {
                                logger.info('[Forward-Fill] Re-forward-fill auto-persisted', {
                                  section,
                                  sourceWeek: mostRecentWeek,
                                  targetWeek: periodKey
                                });
                              }
                            } else {
                              logger.warn('Re-forward-fill auto-persist failed', {
                                section,
                                targetWeek: periodKey
                              }, `useSimpleSnapshot:${section}`);
                            }
                          } else {
                            // Fallback to regular save if no userId or sourceWeek
                            const saveSuccess = await saveSnapshot(periodKey, section, clonedData, workspaceId!);
                            if (saveSuccess) {
                              if (import.meta.env.DEV) {
                                logger.debug('Re-forward-fill auto-persisted (no metadata)', {
                                  section,
                                  sourceWeek: mostRecentWeek,
                                  targetWeek: periodKey
                                }, `useSimpleSnapshot:${section}`);
                              }
                            }
                          }
                        } else {
                          if (import.meta.env.DEV) {
                            logger.debug('Skipping re-forward-fill auto-persist - data not meaningful', {
                              section,
                              sourceWeek: mostRecentWeek,
                              targetWeek: periodKey
                            }, `useSimpleSnapshot:${section}`);
                          }
                          // Don't persist empty/non-meaningful data
                        }
                      }
                    } catch (saveError) {
                      logger.error('Re-forward-fill auto-persist error', {
                        error: saveError,
                        section,
                        targetWeek: periodKey
                      }, `useSimpleSnapshot:${section}`);
                    }
                  }
                  
                  return;
                  }
                }
              }
            }
          }
          
          // Otherwise use existing DB data (no more recent week found)
          // ✅ FIX: Check if data is empty/default - if so, forward-fill from source (for all sections, not just Notes)
          // Reuse isPastWeek variable already calculated above (line 699)
          
          // Check if data is empty for past weeks (handles case where delete left empty rows)
          let isEmpty = false;
          if (section === 'notes' && data !== null && typeof data !== 'undefined') {
            isEmpty = !hasTipTapContent(data);
          } else if (Array.isArray(data) && data.length === 0) {
            isEmpty = true;
          } else if (section === 'funnel' && typeof data === 'object' && data !== null &&
                     (data as any).newCreators === 0 && 
                     (data as any).inSequence === 0 &&
                     (data as any).inNegotiation === 0 &&
                     (data as any).activeCreators === 0) {
            isEmpty = true;
          }
          
          // ✅ FIX: For past weeks with empty data, allow forward-fill (don't return early)
          if (isEmpty && isPastWeek) {
            if (import.meta.env.DEV) {
              logger.debug('Past week has empty data - allowing forward-fill (may be from failed delete)', {
                section,
                targetWeek: periodKey,
                isPastWeek: true,
                isEmpty: true
              }, `useSimpleSnapshot:${section}`);
            }
            // Fall through to forward-fill logic in CASE B
          } else if (section === 'notes' && data !== null && typeof data !== 'undefined') {
            const hasContent = hasTipTapContent(data);
            if (!hasContent) {
              // Target snapshot exists but is empty - forward-fill from source
              if (import.meta.env.DEV) {
                logger.debug('Target snapshot exists but is empty, attempting forward-fill', {
                  section,
                  targetWeek: periodKey
                }, `useSimpleSnapshot:${section}`);
              }
              // Fall through to forward-fill logic below
            } else {
              // Target has meaningful content, use it
              setState({ data: data, isLoading: false });
              lastCommittedRef.current = data;
              hasHydratedRef.current = true;
              return;
            }
          } else if (!isEmpty) {
            // ✅ CRITICAL FIX: Check if week was deleted BEFORE using existing data
            // If week was deleted, treat as if row doesn't exist (allow forward-fill)
            let isDeletedWeek = false;
            try {
              const deletedWeeks = JSON.parse(sessionStorage.getItem('marlo:deletedWeeks') || '[]') as string[];
              isDeletedWeek = deletedWeeks.includes(periodKey);
            } catch (e) {
              // Non-critical
            }
            
            if (isDeletedWeek) {
              // Week was deleted - don't use existing data, allow forward-fill
              if (import.meta.env.DEV) {
                logger.debug('Week was deleted - ignoring existing data, allowing forward-fill', {
                  section,
                  targetWeek: periodKey,
                  rowExists: true,
                  isDeletedWeek: true
                }, `useSimpleSnapshot:${section}`);
              }
              // Don't return - fall through to CASE B forward-fill logic
            } else {
              // Data exists and is not empty - use existing data
              setState({ data: (data ?? defaultValue) as T, isLoading: false });
              lastCommittedRef.current = (data ?? defaultValue) as T;
              hasHydratedRef.current = true;
              return;
            }
          }
          // If empty and past week, OR week was deleted, fall through to CASE B forward-fill logic
        }
        
        // CASE B: No snapshot exists (or Notes snapshot is empty) → attempt forward-fill
        try {
            // ✅ CRITICAL FIX: Only clear cache if week is deleted OR no pending save
            // This prevents clearing cache when save is in progress (which would lose saved data)
            // Check if there's a pending save for this week/section
            const saveKey = `${workspaceId}:${periodKey}:${section}`;
            const hasPendingSave = pendingSaves.current.has(saveKey);
            
            // Only clear cache if:
            // 1. Row doesn't exist AND
            // 2. No pending save (save completed or never started) AND
            // 3. Week is deleted OR we're certain row doesn't exist
            if (!snapshotRowExists && !hasPendingSave) {
              // Check if week was deleted - if so, clear cache to allow forward-fill
              let isDeletedWeek = false;
              try {
                const deletedWeeks = JSON.parse(sessionStorage.getItem('marlo:deletedWeeks') || '[]') as string[];
                isDeletedWeek = deletedWeeks.includes(periodKey);
              } catch (e) {
                // Non-critical
              }
              
              // Only clear cache if week is deleted (to allow forward-fill)
              // Don't clear cache if row might exist but check is stale (race condition)
              if (isDeletedWeek) {
                try {
                  Cache.clear(periodKey, section);
                  if (import.meta.env.DEV) {
                    logger.debug('Cleared cache for deleted week to allow forward-fill', {
                      section,
                      targetWeek: periodKey,
                      rowExists: false,
                      isDeletedWeek: true
                    }, `useSimpleSnapshot:${section}`);
                  }
                } catch (e) {
                  // Non-critical
                }
              }
            } else if (hasPendingSave && import.meta.env.DEV) {
              logger.debug('Skipping cache clear - save in progress', {
                section,
                targetWeek: periodKey,
                rowExists: snapshotRowExists,
                hasPendingSave: true
              }, `useSimpleSnapshot:${section}`);
            }
            
            // ✅ FIX: Single targetDate declaration (removed duplicate)
            const targetDate = getWeekStartDateFromKey(periodKey);
          
          if (!targetDate) {
            if (import.meta.env.DEV) {
              logger.debug('Invalid target week key, skipping forward-fill', {
                target: periodKey,
                section
              }, `useSimpleSnapshot:${section}`);
            }
            // Fall through to default
          } else {
            // For cumulative sections, recursively check previous weeks (up to 12 weeks back)
            // This handles migration gaps where data was moved (e.g., W44 → W48)
            // For non-cumulative sections, strictly use previous week as source
            // ✅ FIX: Past-week check should ONLY block when snapshot row exists (preserve historical data)
            // If row doesn't exist, allow forward-fill even for past weeks (handles migration gaps)
            const currentDate = getWeekStartDateFromKey(getCurrentWeekKey());
            const isPastWeek = targetDate && currentDate && targetDate.getTime() < currentDate.getTime();
            
            // ✅ CRITICAL FIX: Check if week was deleted BEFORE checking hasMeaningfulData
            // If week was deleted, treat as if row doesn't exist (allow forward-fill)
            // This ensures deleted weeks forward-fill even if row still exists in DB
            let isDeletedWeek = false;
            try {
              const deletedWeeks = JSON.parse(sessionStorage.getItem('marlo:deletedWeeks') || '[]') as string[];
              isDeletedWeek = deletedWeeks.includes(periodKey);
            } catch (e) {
              // Non-critical
            }
            
            // ✅ FIX: Early return ONLY when past week AND row exists WITH meaningful data AND week NOT deleted
            // If week was deleted, allow forward-fill even if row exists with data
            // If row exists but is empty, allow forward-fill (handles case where delete left empty rows)
            // This allows forward-fill to proceed for past weeks when row is missing OR empty OR week was deleted
            if (isPastWeek && snapshotRowExists && !isDeletedWeek) {
              // Check if existing data is meaningful (not empty/default)
              const existingData = data;
              let hasMeaningfulData = false;
              
              if (section === 'funnel') {
                // ✅ BUG 1 FIX: Funnel meaningful data is determined by structure, NOT values
                hasMeaningfulData = typeof existingData === 'object' && existingData !== null;
              } else if (section === 'recommendedCreators') {
                hasMeaningfulData = Array.isArray(existingData) && existingData.length > 0;
              } else if (section === 'liveAssets') {
                hasMeaningfulData = Array.isArray(existingData) && existingData.length > 0;
              } else if (section === 'notes') {
                hasMeaningfulData = hasTipTapContent(existingData);
              } else if (section === 'campaignSpend' || section === 'promoCodes') {
                hasMeaningfulData = Array.isArray(existingData) && existingData.length > 0;
              } else {
                hasMeaningfulData = existingData !== null && existingData !== undefined;
              }
              
              // Only block forward-fill if row exists AND has meaningful data AND week NOT deleted
              if (hasMeaningfulData) {
                if (import.meta.env.DEV) {
                  logger.debug('Skipping forward-fill for past week (snapshot row exists with meaningful data, week not deleted)', {
                    section,
                    targetWeek: periodKey,
                    targetDate: targetDate?.toISOString(),
                    currentDate: currentDate?.toISOString(),
                    rowExists: true,
                    hasMeaningfulData: true,
                    isDeletedWeek: false
                  }, `useSimpleSnapshot:${section}`);
                }
                setState({ data: defaultValue, isLoading: false });
                lastCommittedRef.current = defaultValue;
                hasHydratedRef.current = true;
                return; // Prevent forward-fill for past weeks with existing meaningful snapshots (only if not deleted)
              } else {
                // Row exists but is empty - allow forward-fill
                if (import.meta.env.DEV) {
                  logger.debug('Row exists but is empty - allowing forward-fill for past week', {
                    section,
                    targetWeek: periodKey,
                    targetDate: targetDate?.toISOString(),
                    currentDate: currentDate?.toISOString(),
                    rowExists: true,
                    hasMeaningfulData: false
                  }, `useSimpleSnapshot:${section}`);
                }
                // Fall through to forward-fill logic below
              }
            } else if (isPastWeek && snapshotRowExists && isDeletedWeek) {
              // ✅ CRITICAL: Week was deleted - treat as if row doesn't exist, allow forward-fill
              if (import.meta.env.DEV) {
                logger.debug('Week was deleted - allowing forward-fill even though row exists', {
                  section,
                  targetWeek: periodKey,
                  targetDate: targetDate?.toISOString(),
                  currentDate: currentDate?.toISOString(),
                  rowExists: true,
                  isDeletedWeek: true
                }, `useSimpleSnapshot:${section}`);
              }
              // Don't return - fall through to forward-fill logic below
            }
            
            // ✅ FIX: Forward-fill logic runs for BOTH past and future weeks when row doesn't exist
            // Section-type dispatch happens AFTER past-week check (not as else-if chain)
            if (section === 'funnel' || section === 'recommendedCreators' || section === 'notes') {
              // ✅ CRITICAL FIX: Make linear sections recursive (like cumulative sections)
              // This ensures forward-fill works even if immediate previous week is missing
              // Handles migration gaps and ensures all sections forward-fill consistently
              let checkWeek: string | null = getPreviousWeekKey(periodKey);
              let maxDepth = 12; // Same as cumulative sections - covers ~3 months, handles migration gaps
              let depth = 0;
              while (checkWeek && depth < maxDepth) {
                // DB uses same section_key as app (recommendedCreators, funnel, notes)
                const prevResult = await getSnapshot(checkWeek, section, workspaceId!);
                if (prevResult && prevResult.rowExists) {
                  const prevData = prevResult.data;
                  
                  // ✅ FIX: Check if previous week has meaningful data
                  // For funnel: check if not all zeros
                  // For recommendedCreators/notes: check if not empty array/object
                  let hasMeaningfulData = false;
                  if (section === 'funnel') {
                    // ✅ BUG 1 FIX: Funnel meaningful data is determined by structure, NOT values
                    hasMeaningfulData = typeof prevData === 'object' && prevData !== null;
                  } else if (section === 'recommendedCreators') {
                    hasMeaningfulData = Array.isArray(prevData) && prevData.length > 0;
                  } else if (section === 'notes') {
                    hasMeaningfulData = hasTipTapContent(prevData);
                  } else {
                    hasMeaningfulData = prevData !== null && prevData !== undefined;
                  }
                  
                  if (hasMeaningfulData) {
                    let cloned = JSON.parse(JSON.stringify(prevData));
                    if (section === 'funnel') {
                      cloned = normalizeFunnelData(cloned);
                    }
                    setState({ data: cloned, isLoading: false });
                    lastCommittedRef.current = cloned;
                    hasHydratedRef.current = true;
                    try { 
                      Cache.set(periodKey, section, cloned); 
                    } catch (error) {
                      if (import.meta.env.DEV) {
                        logger.debug('Cache operation failed (non-critical)', { error, section, periodKey }, `useSimpleSnapshot:${section}`);
                      }
                    }
                    
                    // ✅ CRITICAL FIX: Check if week was deleted (prevent auto-persistence)
                    // This check must run in BOTH DEV and PROD to prevent forward-fill from recreating deleted weeks
                    let isDeletedWeek = false;
                    try {
                      const deletedWeeks = JSON.parse(sessionStorage.getItem('marlo:deletedWeeks') || '[]') as string[];
                      isDeletedWeek = deletedWeeks.includes(periodKey);
                    } catch (e) {
                      // Non-critical
                    }
                    
                    // ✅ CRITICAL FIX: Persist forward-fill when target row doesn't exist, UNLESS week was deleted
                    // For linear sections (notes, funnel, recommendedCreators), check if target row exists
                    // If not, persist even for past weeks to ensure data survives refresh
                    // BUT: Do NOT persist if week was explicitly deleted (user wants it blank)
                    const shouldPersist = AUTO_PERSIST_FORWARD_FILL && userId && !snapshotRowExists && !isDeletedWeek;
                    
                    // ✅ PHASE 3: Comprehensive forward-fill decision logging
                    if (import.meta.env.DEV) {
                      logger.debug('[Phase3:ForwardFillDecision]', {
                        section,
                        targetWeek: periodKey,
                        isPastWeek,
                        snapshotRowExists,
                        chosenSourceWeek: checkWeek,
                        depth,
                        willPersist: shouldPersist,
                        cacheKey: `dash:${periodKey}:${section}`,
                        why: isDeletedWeek ? 'Week was deleted (user wants blank)' : 
                             !userId ? 'No userId' : 
                             snapshotRowExists ? 'Row exists (preserve history)' :
                             !AUTO_PERSIST_FORWARD_FILL ? 'AUTO_PERSIST disabled' :
                             'Will persist forward-fill'
                      }, `useSimpleSnapshot:${section}`);
                    }
                    
                    if (shouldPersist) {
                      try {
                        await saveSnapshotWithForwardFillMetadata(periodKey, section, cloned, checkWeek, userId, workspaceId!);
                        
                        // ✅ CRITICAL FIX: DO NOT remove week from deleted list when forward-fill persists
                        // If user deleted a week, they want it blank - don't repopulate it automatically
                        // The week should remain in deleted list to prevent future auto-persistence
                        // User can manually add data if they want to repopulate the week
                      
                      // ✅ PHASE 3: Log persistence
                      if (import.meta.env.DEV) {
                        logger.debug('[Phase3:ForwardFillPersist]', {
                          section,
                          targetWeek: periodKey,
                          sourceWeek: checkWeek,
                          didPersist: true,
                          chosenSourceWeek: checkWeek
                        }, `useSimpleSnapshot:${section}`);
                      }
                        
                        if (import.meta.env.DEV) {
                          logger.debug('[ForwardFill] Persisted forward-fill (linear, recursive, missing row)', {
                            section,
                            targetWeek: periodKey,
                            sourceWeek: checkWeek,
                            depth,
                            isPastWeek,
                            rowExistedBefore: snapshotRowExists,
                            persisted: true
                          }, `useSimpleSnapshot:${section}`);
                        }
                      } catch (error) {
                        if (import.meta.env.DEV) {
                          logger.error('[ForwardFill] Failed to persist forward-fill', {
                            section,
                            targetWeek: periodKey,
                            sourceWeek: checkWeek,
                            error
                          }, `useSimpleSnapshot:${section}`);
                        }
                      }
                    } else if (import.meta.env.DEV) {
                      logger.debug('[Phase3:ForwardFillPersist]', {
                        section,
                        targetWeek: periodKey,
                        sourceWeek: checkWeek,
                        didPersist: false,
                        chosenSourceWeek: checkWeek,
                        reason: isDeletedWeek ? 'Week was deleted' : snapshotRowExists ? 'Row exists (preserve history)' : !userId ? 'No userId' : 'AUTO_PERSIST disabled'
                      }, `useSimpleSnapshot:${section}`);
                    }
                    return; // Found data, stop searching
                  }
                }
                // Continue to previous week
                checkWeek = getPreviousWeekKey(checkWeek);
                depth++;
              }
              
              // No data found after recursive search
              if (import.meta.env.DEV) {
                logger.debug('[ForwardFill] No data found after recursive search (linear)', {
                  section,
                  targetWeek: periodKey,
                  maxDepth,
                  depth
                }, `useSimpleSnapshot:${section}`);
              }
              // ✅ FIX: Set default state so linear sections don't stay blank when no forward-fill source found
              // This ensures sections show empty/default state instead of staying in loading state
              setState({ data: defaultValue, isLoading: false });
              lastCommittedRef.current = defaultValue;
              hasHydratedRef.current = true;
            } else if (section === 'liveAssets' || section === 'campaignSpend' || section === 'promoCodes') {
              // ✅ PERFORMANCE: Single getSnapshotsBySection instead of up to 12 sequential getSnapshot calls
              const sectionSnapshotsCum = await getSnapshotsBySection(section, periodKey, 12);
              const targetDateCum = getWeekStartDateFromKey(periodKey);
              let chosenSourceWeek: string | null = null;
              let sourceDataCum: unknown = null;
              if (sectionSnapshotsCum?.length && targetDateCum) {
                for (const s of sectionSnapshotsCum) {
                  const sDate = getWeekStartDateFromKey(s.week_key);
                  if (!sDate || sDate.getTime() >= targetDateCum.getTime()) continue;
                  const meaningful = hasMeaningfulData(s.data, section);
                  if (meaningful && (!chosenSourceWeek || (getWeekStartDateFromKey(chosenSourceWeek) && sDate.getTime() > getWeekStartDateFromKey(chosenSourceWeek)!.getTime()))) {
                    chosenSourceWeek = s.week_key;
                    sourceDataCum = s.data;
                  }
                }
              }
              if (chosenSourceWeek && sourceDataCum !== null && sourceDataCum !== undefined) {
                let cloned = JSON.parse(JSON.stringify(sourceDataCum));
                if ((section === 'liveAssets' || section === 'campaignSpend' || section === 'promoCodes') && Array.isArray(cloned)) {
                  const beforeCount = cloned.length;
                  cloned = cloned.filter((item: Record<string, unknown>) => {
                    if (section === 'liveAssets') {
                      return !item.hidden;
                    }
                    return !item.deleted;
                  });
                  if (beforeCount !== cloned.length) {
                    if (import.meta.env.DEV) {
                      logger.debug('Filtered hidden/deleted items during forward-fill (no snapshot row)', {
                        before: beforeCount,
                        after: cloned.length,
                        sourceWeek: chosenSourceWeek,
                        targetWeek: periodKey
                      }, `useSimpleSnapshot:${section}`);
                    } else {
                      logger.info('[Forward-Fill] Filtered hidden/deleted items', {
                        section,
                        filtered: beforeCount - cloned.length,
                        sourceWeek: chosenSourceWeek,
                        targetWeek: periodKey
                      });
                    }
                  }
                }
                if (import.meta.env.DEV) {
                  logger.debug('[ForwardFill] Decision (cumulative, batch getSnapshotsBySection)', {
                    section,
                    targetWeek: periodKey,
                    isPastWeek: isPastWeek || false,
                    rowExists: snapshotRowExists,
                    chosenSourceWeek,
                    meaningfulData: true,
                    itemCount: Array.isArray(cloned) ? cloned.length : 'data'
                  }, `useSimpleSnapshot:${section}`);
                } else {
                  logger.info('[Forward-Fill] SUCCESS (cumulative section)', {
                    section,
                    sourceWeek: chosenSourceWeek,
                    targetWeek: periodKey,
                    itemCount: Array.isArray(cloned) ? cloned.length : 'data'
                  });
                }
                setState({ data: cloned, isLoading: false });
                lastCommittedRef.current = cloned;
                hasHydratedRef.current = true;
                try {
                  Cache.set(periodKey, section, cloned);
                } catch (error) {
                  if (import.meta.env.DEV) {
                    logger.debug('Cache operation failed (non-critical)', { error, section, periodKey }, `useSimpleSnapshot:${section}`);
                  }
                }
                let isDeletedWeek = false;
                try {
                  const deletedWeeks = JSON.parse(sessionStorage.getItem('marlo:deletedWeeks') || '[]') as string[];
                  isDeletedWeek = deletedWeeks.includes(periodKey);
                } catch (e) {
                  // Non-critical
                }
                const shouldPersist = AUTO_PERSIST_FORWARD_FILL && userId && !snapshotRowExists && !isDeletedWeek;
                if (shouldPersist) {
                  try {
                    await saveSnapshotWithForwardFillMetadata(periodKey, section, cloned, chosenSourceWeek, userId, workspaceId!);
                    if (import.meta.env.DEV) {
                      logger.debug('[ForwardFill] Persisted forward-fill (cumulative, missing row)', {
                        section,
                        targetWeek: periodKey,
                        sourceWeek: chosenSourceWeek,
                        isPastWeek,
                        rowExistedBefore: snapshotRowExists
                      }, `useSimpleSnapshot:${section}`);
                    }
                  } catch (error) {
                    if (import.meta.env.DEV) {
                      logger.error('[ForwardFill] Failed to persist forward-fill', {
                        section,
                        targetWeek: periodKey,
                        sourceWeek: chosenSourceWeek,
                        error
                      }, `useSimpleSnapshot:${section}`);
                    }
                  }
                } else if (import.meta.env.DEV) {
                  logger.debug('[ForwardFill] Skipped persist (cumulative, UI-only)', {
                    section,
                    targetWeek: periodKey,
                    sourceWeek: chosenSourceWeek,
                    reason: snapshotRowExists ? 'Row exists (preserve history)' : !userId ? 'No userId' : 'AUTO_PERSIST disabled'
                  }, `useSimpleSnapshot:${section}`);
                }
                return;
              }
              if (import.meta.env.DEV && !snapshotRowExists && !chosenSourceWeek) {
                let isDeletedWeek = false;
                try {
                  const deletedWeeks = JSON.parse(sessionStorage.getItem('marlo:deletedWeeks') || '[]') as string[];
                  isDeletedWeek = deletedWeeks.includes(periodKey);
                } catch (e) {
                  // Non-critical
                }
                if (isDeletedWeek) {
                  logger.warn('[REGRESSION TEST] Forward-fill failed for deleted week', {
                    section,
                    targetWeek: periodKey,
                    isPastWeek: isPastWeek || false,
                    rowExists: snapshotRowExists,
                    chosenSourceWeek: null,
                    meaningfulData: false,
                    reason: 'No meaningful data found within 12 weeks'
                  }, `useSimpleSnapshot:${section}`);
                }
              }
              if (import.meta.env.DEV && !chosenSourceWeek) {
                logger.debug('Forward-fill decision', {
                  section,
                  targetWeek: periodKey,
                  isPastWeek: isPastWeek || false,
                  rowExists: snapshotRowExists,
                  chosenSourceWeek: null,
                  meaningfulData: false,
                  reason: 'No meaningful data found within 12 weeks'
                }, `useSimpleSnapshot:${section}`);
              }
            }
            // Legacy fallback: Fetch snapshots for this section, filtered to weeks before target (optimized query)
            // This is now only used for edge cases that don't match linear or cumulative sections
            const sectionSnapshots = await getSnapshotsBySection(section, periodKey, 20);
            
            if (loadSeq.current !== seq) return; // stale check
            
            // Log in both dev and production
            if (import.meta.env.DEV && targetDate) {
              logger.debug('getSnapshotsBySection result', {
                section,
                targetWeek: periodKey,
                targetDate: targetDate.toISOString(),
                found: sectionSnapshots?.length || 0,
                workspaceId,
                snapshots: sectionSnapshots?.map(s => {
                  const sDate = getWeekStartDateFromKey(s.week_key);
                  return {
                    week: s.week_key,
                    weekDate: sDate?.toISOString() || 'INVALID',
                    hasData: s.data !== null && s.data !== undefined,
                    dataType: typeof s.data,
                    isArray: Array.isArray(s.data),
                    dataKeys: typeof s.data === 'object' && !Array.isArray(s.data) ? Object.keys(s.data || {}) : 'N/A',
                    meaningful: hasMeaningfulData(s.data, section),
                    dataPreview: section === 'funnel' ? s.data : section === 'notes' ? { type: (s.data as any)?.type, hasContent: Array.isArray((s.data as any)?.content) } : 'N/A',
                    rawData: section === 'funnel' ? JSON.stringify(s.data).substring(0, 200) : 'N/A',
                    dateBeforeTarget: sDate && targetDate ? sDate.getTime() < targetDate.getTime() : false
                  };
                }) || []
              }, `useSimpleSnapshot:${section}`);
            } else {
              // Production logging (use console for visibility)
              logger.info('[Forward-Fill] getSnapshotsBySection result', {
                section,
                targetWeek: periodKey,
                found: sectionSnapshots?.length || 0,
                meaningfulSnapshots: sectionSnapshots?.filter(s => hasMeaningfulData(s.data, section)).map(s => ({
                  week: s.week_key,
                  meaningful: true,
                  dataType: typeof s.data,
                  isArray: Array.isArray(s.data)
                })) || []
              });
            }
            
            if (!sectionSnapshots?.length) {
              if (import.meta.env.DEV) {
                logger.debug('No snapshots found for forward-fill', {
                  section,
                  targetWeek: periodKey
                }, `useSimpleSnapshot:${section}`);
              }
              // Fall through to default
            } else {
              // Find the most recent week STRICTLY BEFORE target with meaningful data
              // Strategy: First try to find meaningful data, but if none exists, use the most recent week with ANY data
              let sourceWeek: string | null = null;
              let sourceData: unknown = null;
              let sourceWeekFallback: string | null = null;
              let sourceDataFallback: unknown = null;
              
              if (import.meta.env.DEV && targetDate) {
                logger.debug('Searching for forward-fill source', {
                  section,
                  targetWeek: periodKey,
                  targetDate: targetDate.toISOString(),
                  snapshotsToCheck: sectionSnapshots.length,
                  snapshotWeeks: sectionSnapshots.map(s => s.week_key)
                }, `useSimpleSnapshot:${section}`);
              }
              
              if (!targetDate) {
                // Fall through to default if targetDate is invalid
                setState({ data: defaultValue, isLoading: false });
                lastCommittedRef.current = defaultValue;
                hasHydratedRef.current = true;
                return;
              }
              
              for (const snapshot of sectionSnapshots) {
                const snapshotDate = getWeekStartDateFromKey(snapshot.week_key);
                if (!snapshotDate || snapshotDate.getTime() >= targetDate.getTime()) {
                  continue; // Skip same week or future weeks
                }
                
                // Check if this snapshot has meaningful data (not empty)
                const snapshotHasMeaningfulData = hasMeaningfulData(snapshot.data, section);
                
                // Track the most recent week with ANY data (fallback)
                if (snapshot.data !== null && snapshot.data !== undefined) {
                  if (!sourceWeekFallback || snapshotDate.getTime() > getWeekStartDateFromKey(sourceWeekFallback)!.getTime()) {
                    sourceWeekFallback = snapshot.week_key;
                    sourceDataFallback = snapshot.data;
                  }
                }
                
                // Log meaningful snapshots in production too
                if (snapshotHasMeaningfulData) {
                  if (import.meta.env.DEV) {
                    logger.debug('Checking snapshot for forward-fill', {
                      snapshotWeek: snapshot.week_key,
                      snapshotDate: snapshotDate.toISOString(),
                      hasMeaningfulData: snapshotHasMeaningfulData,
                      hasAnyData: snapshot.data !== null && snapshot.data !== undefined,
                      dataType: Array.isArray(snapshot.data) ? 'array' : typeof snapshot.data,
                      dataLength: Array.isArray(snapshot.data) ? snapshot.data.length : Object.keys(snapshot.data || {}).length,
                      rawData: JSON.stringify(snapshot.data).substring(0, 200),
                      section,
                      dateComparison: targetDate && snapshotDate.getTime() < targetDate.getTime() ? 'BEFORE' : 'SAME_OR_AFTER'
                    }, `useSimpleSnapshot:${section}`);
                  } else {
                    logger.info('[Forward-Fill] Checking meaningful snapshot', {
                      snapshotWeek: snapshot.week_key,
                      hasMeaningfulData: true,
                      dataType: Array.isArray(snapshot.data) ? 'array' : typeof snapshot.data,
                      dateBeforeTarget: targetDate ? snapshotDate.getTime() < targetDate.getTime() : false
                    });
                  }
                }
                
                // Prefer meaningful data over fallback
                if (snapshotHasMeaningfulData) {
                  // Found a valid source week (strictly before target with meaningful data)
                  if (!sourceWeek || snapshotDate.getTime() > getWeekStartDateFromKey(sourceWeek)!.getTime()) {
                    sourceWeek = snapshot.week_key;
                    sourceData = snapshot.data;
                    
                    // Log in both dev and production
                    if (import.meta.env.DEV) {
                      logger.debug('Found new best source week (meaningful)', {
                        sourceWeek,
                        sourceDate: snapshotDate.toISOString(),
                        targetWeek: periodKey,
                        targetDate: targetDate?.toISOString()
                      }, `useSimpleSnapshot:${section}`);
                    } else {
                      logger.info('[Forward-Fill] Found best source week', {
                        sourceWeek,
                        targetWeek: periodKey,
                        section
                      });
                    }
                  }
                }
              }
              
              // Use meaningful data if found, otherwise use fallback (most recent with any data)
              if (!sourceWeek && sourceWeekFallback) {
                sourceWeek = sourceWeekFallback;
                sourceData = sourceDataFallback;
                
                if (import.meta.env.DEV) {
                  logger.debug('Using fallback source week (most recent with any data)', {
                    sourceWeek,
                    targetWeek: periodKey
                  }, `useSimpleSnapshot:${section}`);
                }
              }
              
              // Forward-fill if source found
              if (sourceWeek && sourceData !== null && typeof sourceData !== 'undefined') {
                // Deep clone to prevent reference sharing
                let clonedData = JSON.parse(JSON.stringify(sourceData));
                
                // Normalize funnel data format: convert array format to object format
                // This ensures consistent data format across all weeks
                if (section === 'funnel') {
                  clonedData = normalizeFunnelData(clonedData);
                  
                  if (import.meta.env.DEV) {
                    logger.debug('Normalized funnel data during forward-fill', {
                      sourceWeek,
                      targetWeek: periodKey,
                      originalFormat: Array.isArray(sourceData) ? 'array' : 'object',
                      normalizedData: clonedData
                    }, `useSimpleSnapshot:${section}`);
                  }
                }
                
                // ✅ FIX: Hidden assets should forward-fill (they're filtered on client for viewers)
                // Only filter deleted items for Campaign Spend & Promo Codes (handled in RPC)
                
                // Sanitize TipTap documents before forward-filling to prevent TipTap errors
                if (section === 'notes' && !validateTipTapDocument(clonedData)) {
                  if (import.meta.env.DEV) {
                    logger.warn('Sanitizing invalid TipTap document before forward-fill', {
                      section: 'notes',
                      sourceWeek,
                      targetWeek: periodKey,
                      originalPreview: JSON.stringify(clonedData).substring(0, 200)
                    }, `useSimpleSnapshot:${section}`);
                  }
                  clonedData = sanitizeTipTapDocument(clonedData);
                }
                
                // Log in both dev and production
                const itemCount = Array.isArray(clonedData) ? clonedData.length : 'data';
                if (import.meta.env.DEV) {
                  logger.debug('Forward-fill successful', {
                    section,
                    sourceWeek,
                    targetWeek: periodKey,
                    itemCount,
                    autoPersist: AUTO_PERSIST_FORWARD_FILL,
                    wasSanitized: section === 'notes' && !validateTipTapDocument(sourceData)
                  }, `useSimpleSnapshot:${section}`);
                } else {
                  logger.info('[Forward-Fill] SUCCESS', {
                    section,
                    sourceWeek,
                    targetWeek: periodKey,
                    itemCount,
                    autoPersist: AUTO_PERSIST_FORWARD_FILL
                  });
                }
                
                // Update state immediately
                setState({ data: clonedData, isLoading: false });
                lastCommittedRef.current = clonedData;
                hasHydratedRef.current = true;
                // Cache forward-filled data
                try {
                  Cache.set(periodKey, section, clonedData);
                } catch (e) {
                  // Ignore cache errors
                }
                
                // Auto-persist if enabled (default behavior)
                if (AUTO_PERSIST_FORWARD_FILL) {
                  try {
                    // CRITICAL: Only save if we actually found source data AND it's meaningful
                    if (sourceWeek && sourceData !== null && typeof sourceData !== 'undefined') {
                      // Double-check the data is meaningful before persisting
                      if (hasMeaningfulData(clonedData, section)) {
                        if (userId && sourceWeek) {
                          const saveSuccess = await saveSnapshotWithForwardFillMetadata(
                            periodKey,
                            section,
                            clonedData,
                            sourceWeek,
                            userId,
                            workspaceId!
                          );
                          if (saveSuccess) {
                            if (import.meta.env.DEV) {
                              logger.debug('Forward-fill auto-persisted with metadata', {
                                section,
                                sourceWeek,
                                targetWeek: periodKey
                              }, `useSimpleSnapshot:${section}`);
                            } else {
                              logger.info('[Forward-Fill] Auto-persisted', {
                                section,
                                sourceWeek,
                                targetWeek: periodKey
                              });
                            }
                          } else {
                            logger.warn('Forward-fill auto-persist failed', {
                              section,
                              targetWeek: periodKey
                            }, `useSimpleSnapshot:${section}`);
                          }
                        } else {
                          // Fallback to regular save if no userId or sourceWeek
                          const saveSuccess = await saveSnapshot(periodKey, section, clonedData, workspaceId!);
                          if (saveSuccess) {
                            if (import.meta.env.DEV) {
                              logger.debug('Forward-fill auto-persisted (no metadata)', {
                                section,
                                sourceWeek,
                                targetWeek: periodKey
                              }, `useSimpleSnapshot:${section}`);
                            } else {
                              logger.info('[Forward-Fill] Auto-persisted', {
                                section,
                                sourceWeek,
                                targetWeek: periodKey
                              });
                            }
                          }
                        }
                      } else {
                        if (import.meta.env.DEV) {
                          logger.debug('Skipping auto-persist - data not meaningful', {
                            section,
                            sourceWeek,
                            targetWeek: periodKey
                          }, `useSimpleSnapshot:${section}`);
                        }
                        // Don't persist empty/non-meaningful data
                      }
                    }
                  } catch (saveError) {
                    logger.error('Forward-fill auto-persist error', {
                      error: saveError,
                      section,
                      targetWeek: periodKey
                    }, `useSimpleSnapshot:${section}`);
                  }
                }
                
                return; // Don't set to defaultValue
              } else {
                if (import.meta.env.DEV) {
                  logger.debug('No valid source week found for forward-fill', {
                    section,
                    targetWeek: periodKey,
                    snapshotsChecked: sectionSnapshots.length
                  }, `useSimpleSnapshot:${section}`);
                }
              }
            }
          }
        } catch (forwardFillError) {
          logger.error('Forward-fill failed', {
            error: forwardFillError,
            section,
            periodKey
          }, `useSimpleSnapshot:${section}`);
        }
        
        // CASE C: No snapshot and no forward-fill available → use default
        setState({ data: defaultValue, isLoading: false });
        lastCommittedRef.current = defaultValue;
        hasHydratedRef.current = true;
      } catch (error) {
        if (loadSeq.current === seq) {
          // Load failed: keep existing state, do not overwrite with defaultValue
          setState(prev => ({ ...prev, isLoading: false }));
          setInitialMountComplete(true);
        }
      }
    })();
  }, [workspaceReady, enabled, workspaceId, section, periodKey, viewerMode]);

  // Unmount cleanup: last-resort only. Do NOT rely on this for correctness.
  // Navigation is immediate (fire-and-forget flush); DB write is best-effort. Cache is written optimistically on flush.
  // This is best-effort only (no await, no cache update); React cleanup cannot guarantee completion.
  useEffect(() => {
    return () => {
      const pending = pendingSaveDataRef.current;
      const currentWorkspaceId = workspaceIdRef.current;
      if (
        didUserInitiateSaveRef.current &&
        pending &&
        currentWorkspaceId &&
        !isEqual(pending.data, lastCommittedRef.current)
      ) {
        saveSnapshot(pending.periodKey, pending.section, pending.data, currentWorkspaceId)
          .then(() => {
            try {
              Cache.clear(pending.periodKey, pending.section);
            } catch {
              // ignore
            }
          })
          .catch(() => {});
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, []);

  // Invalidate cache and reload when viewerMode changes (security fix)
  // This ensures viewers see correct data when switching from admin mode
  useEffect(() => {
    const previousViewerMode = previousViewerModeRef.current;
    previousViewerModeRef.current = viewerMode;

    if (!workspaceReady) return;
    if (previousViewerMode === viewerMode) return;
    
    // Clear cache for current section/week when viewerMode changes
    try {
      Cache.clear(periodKey, section);
    } catch (e) {
      // Ignore cache errors
    }
    
    // Reset hydration state to force reload with correct permissions
    hasHydratedRef.current = false;
    setInitialMountComplete(false);
    
    // Increment load sequence to trigger reload
    loadSeq.current += 1;
    
    if (import.meta.env.DEV) {
      logger.debug('Viewer mode changed, invalidating cache and reloading', { 
        section, 
        periodKey, 
        viewerMode 
      }, `useSimpleSnapshot:${section}`);
    }
  }, [viewerMode, workspaceReady, periodKey, section]);

  // ✅ STEP 2.2: Cross-tab synchronization via StorageEvent
  // Listen for localStorage changes from other tabs and reload data if needed
  useEffect(() => {
    if (!workspaceReady || !enabled) return;

    const handleStorageChange = async (e: StorageEvent) => {
      // Only handle changes to our cache namespace
      if (!e.key || !e.key.startsWith('dash:')) return;
      
      // Extract week and section from key (format: dash:WEEK_KEY:SECTION)
      const keyParts = e.key.split(':');
      if (keyParts.length !== 3) return; // Invalid format
      
      const changedWeekKey = keyParts[1];
      const changedSection = keyParts[2];
      
      // Only reload if this is the current week/section
      if (changedWeekKey !== periodKey || changedSection !== section) return;
      
      // ✅ Data was updated in another tab - reload it
      // Note: StorageEvent doesn't fire for the tab that made the change, so this is safe
      if (import.meta.env.DEV) {
        logger.debug('Cross-tab cache update detected', { 
          weekKey: changedWeekKey, 
          section: changedSection 
        }, `useSimpleSnapshot:${section}`);
      }
      
      // Show notification to user
      showInfoToast(
        'Data Updated',
        'This section was updated in another tab. Reloading...',
        3000
      );
      
      // Reload data from cache (it was just updated by the other tab)
      try {
        const updatedCache = Cache.get(periodKey, section);
        if (updatedCache.value !== undefined) {
          setState({ data: updatedCache.value as T, isLoading: false });
          dataRef.current = updatedCache.value as T;
          lastCommittedRef.current = updatedCache.value as T;
          
          // If cache is fresh, mark as hydrated (don't trigger new fetch)
          if (updatedCache.fresh) {
            hasHydratedRef.current = true;
          } else {
            // Cache is stale, trigger background refresh
            hasHydratedRef.current = false;
            // Trigger reload by resetting hydration flag (will be picked up by load effect)
          }
        }
      } catch (error) {
        logger.error('Failed to reload data from cross-tab update', { 
          error, 
          section, 
          periodKey 
        }, `useSimpleSnapshot:${section}`);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [workspaceReady, enabled, periodKey, section]);

  // Immediate state update + debounced database save
  const save = useCallback(async (next: T) => {
    if (!workspaceReady) {
      return; // Only check workspace, not hydration
    }
    
    // ✅ ARCHITECTURAL: Mark save as user-initiated for ALL sections
    didUserInitiateSaveRef.current = true;
    
    // ✅ CRITICAL: Optimistic cache ONLY for Notes (Google Docs-like behavior)
    // Other sections (cumulative, business-critical) only update cache AFTER confirmed save
    // This prevents optimistic masking of save failures for LiveAssets, CampaignSpend, PromoCodes
    if (section === 'notes') {
      try {
        console.log(`[useSimpleSnapshot] ${section} optimistic cache write (pre-save)`, {
          periodKey,
          section,
          dataPreview: JSON.stringify(next).substring(0, 100),
          timestamp: new Date().toISOString()
        });
        Cache.set(periodKey, section, next);
      } catch (error) {
        console.error(`[useSimpleSnapshot] Optimistic cache write failed`, { error, periodKey, section });
        // ignore cache errors (non-critical)
      }
    }
    
    // ✅ CRITICAL FIX: Allow optimistic saves even if not fully hydrated
    // If not hydrated yet, mark as hydrated to allow saves
    // This enables users to save data immediately without waiting for initial load
    // This fixes the issue where data disappears on refresh for most sections
    if (!hasHydratedRef.current) {
      hasHydratedRef.current = true;
    }
    
    // ✅ CRITICAL FIX: Ensure initialMountComplete is set when save is called directly
    // This ensures save indicator shows for sections like Notes that call save() directly
    // Without this, save indicator won't show because initialMountComplete is false
    if (!initialMountComplete) {
      setInitialMountComplete(true);
    }

    // ✅ BUG 2 FIX: Mark as user-initiated if data changed from current state
    // For Funnel and Notes, use object reference check to catch all-zero edits and TipTap normalization
    // This is the signal that user made an edit, regardless of value equality
    // Note: For Notes, we already set didUserInitiateSaveRef above, but this check still runs for Funnel and other sections
    const isUserEdit = (section === 'funnel' || section === 'notes')
      ? (state.data !== next) // Object reference check for Funnel and Notes
      : !isEqual(state.data, next); // Value equality for other sections
    if (isUserEdit) {
      didUserInitiateSaveRef.current = true;
    }

    // ✅ BUG 1 FIX: Always update state to ensure new object reference
    // This prevents state collapse when all-zero Funnel data normalizes
    setState(prev => ({ ...prev, data: next }));

    // ✅ BUG 1 FIX: For Funnel and Notes, never skip save based on value equality
    // All-zero Funnel is valid data and must be saved
    // Notes content can normalize (TipTap JSON) making deep equality unreliable
    // Only skip if it's the exact same object reference (not value equality)
    if (section === 'funnel' || section === 'notes') {
      // For Funnel and Notes, always save if it's a different object reference
      // This ensures all-zero saves and normalized Notes content are never skipped
      if (lastCommittedRef.current === next) {
        // Emit save-complete for any section when skip (same ref) so indicator shows
        if (didUserInitiateSaveRef.current) {
          didUserInitiateSaveRef.current = false;
          notifySaveEnd();
        }
        return; // Same object reference, skip
      }
    } else {
      // For other sections, use value equality check
      if (isEqual(lastCommittedRef.current, next)) {
        if (didUserInitiateSaveRef.current) {
          didUserInitiateSaveRef.current = false;
          notifySaveEnd();
        }
        return;
      }
    }

    // ✅ Rate limiting: Prevent abuse while allowing normal usage
    // Conservative limit: 30 saves per minute per section (allows rapid editing)
    const rateLimitKey = `save:${workspaceId}:${periodKey}:${section}`;
    const maxSavesPerMinute = 30;
    const rateLimitWindowMs = 60000; // 1 minute
    
    if (!rateLimiter.isAllowed(rateLimitKey, maxSavesPerMinute, rateLimitWindowMs)) {
      // Show user-friendly message (only in dev or if user is actively editing)
      if (import.meta.env.DEV) {
        logger.warn('Rate limit exceeded', { 
          section, 
          periodKey, 
          rateLimitKey 
        }, `useSimpleSnapshot:${section}`);
      }
      // Note: We don't show toast here to avoid spam - debounce already handles rapid saves
      // Rate limit is a safety net, not a user-facing feature
      return; // Block save attempt
    }

    // ✅ CRITICAL FIX: Increment sequence number and track latest save data
    // This ensures that when user types rapidly, we always save the LATEST data, not intermediate states
    const currentSequence = ++saveSequenceRef.current;
    const currentPeriodKey = periodKey; // Capture current periodKey
    const currentSection = section; // Capture current section
    // ✅ CRITICAL FIX: Update pending save data ref with latest content
    // This ensures that if user types rapidly, we always save the LATEST data, not stale closure values
    pendingSaveDataRef.current = {
      data: next,
      periodKey: currentPeriodKey,
      section: currentSection,
      sequence: currentSequence
    };

    // Clear existing timeout (but keep the latest data in pendingSaveDataRef)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // ✅ ARCHITECTURAL FIX (per ChatGPT recommendation):
    // "Debounce should remain reasonable (100-250ms) and used only for typing 
    //  efficiency, not data integrity. The invariant must be: no dirty snapshot 
    //  state survives a context change."
    // 
    // CORRECTED APPROACH:
    // - Debounce = typing efficiency (reduce DB spam while typing)
    // - Flush = correctness guarantee (all sections flush on context change)
    // - flushAll() called synchronously on: tab change, week change, unmount
    // 
    // WHY 150ms IS OPTIMAL:
    // - Average keystroke interval: 120-200ms (standard typing)
    // - 150ms captures natural pauses without feeling laggy
    // - Max save rate: ~7 saves/sec (reasonable DB load)
    // - Typing efficiency preserved, correctness via flush (not debounce speed)
    // 
    // CORRECTNESS LAYERS:
    // 1. Typing → 150ms debounce (efficiency, not correctness)
    // 2. Context change (tab/week) → synchronous flushAll() (correctness guarantee)
    // 3. Blur event → immediate flush (UX bonus for Notes)
    // 4. Unmount → best-effort save (safety net)
    const debounceMs = 150; // Reasonable debounce for typing efficiency
    
    // Set new timeout for debounced database save
    saveTimeoutRef.current = setTimeout(async () => {
      // Emit save-complete on early exit for any section so indicator still shows
      const emitSaveEndIfUserInitiated = () => {
        if (didUserInitiateSaveRef.current) {
          didUserInitiateSaveRef.current = false;
          notifySaveEnd();
        }
      };

      // ✅ FIX: Check if this save is still valid
      if (!isMountedRef.current) {
        emitSaveEndIfUserInitiated();
        return; // Component unmounted, don't save
      }
      
      // ✅ CRITICAL FIX: Get the LATEST pending save data (not stale closure value)
      // If user typed rapidly, pendingSaveDataRef will have the latest data
      const latestPendingSave = pendingSaveDataRef.current;
      if (!latestPendingSave) {
        emitSaveEndIfUserInitiated();
        return; // No pending save data
      }
      
      // ✅ CRITICAL FIX: Only proceed if this is still the latest save sequence
      // If a newer save came in, we should skip this one (the newer one will save the latest data)
      if (latestPendingSave.sequence !== saveSequenceRef.current) {
        emitSaveEndIfUserInitiated();
        return; // Newer save pending, skip this one (it will save the latest data)
      }
      
      // ✅ CRITICAL FIX: Use the LATEST data from pendingSaveDataRef, not stale closure value
      const dataToSave = latestPendingSave.data;
      const dataPeriodKey = latestPendingSave.periodKey;
      const dataSection = latestPendingSave.section;
      
      if (dataPeriodKey !== periodKey || dataSection !== section) {
        emitSaveEndIfUserInitiated();
        return; // Period or section changed, skip this save
      }

      // ✅ Check for pending save to prevent duplicate saves
      const saveKey = `${workspaceId}:${dataPeriodKey}:${dataSection}`;
      const pendingSave = pendingSaves.current.get(saveKey);
      if (pendingSave) {
        // Save already in progress, wait for it
        try {
          await pendingSave;
        } catch (error) {
          // Ignore errors from pending save, we'll try again
        }
        // ✅ CRITICAL FIX: After waiting, check if this is still the latest save
        // If a newer save came in while we were waiting, skip this one
        const stillLatest = pendingSaveDataRef.current?.sequence === latestPendingSave.sequence;
        if (!stillLatest) {
          emitSaveEndIfUserInitiated();
          return; // Newer save came in, skip this one
        }
      }

      // ✅ FIX: Compute didDataChangeAtSaveTime at execution time (not at save() call time)
      // This ensures we compare against the CURRENT lastCommittedRef, not a stale snapshot
      // CRITICAL: This comparison must happen BEFORE any updates to lastCommittedRef.current
      const didDataChangeAtSaveTime = !isEqual(dataToSave, lastCommittedRef.current);
      // #region agent log (dev only — avoids CSP connect-src violations in production)
      if (import.meta.env.DEV) {
        fetch('http://127.0.0.1:7242/ingest/9f1fa8c7-8ce8-473c-bb6b-bdbbaff33d50',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useSimpleSnapshot.ts:2457',message:'didDataChangeAtSaveTime computed',data:{didDataChangeAtSaveTime,dataToSave:JSON.stringify(dataToSave).substring(0,100),lastCommitted:JSON.stringify(lastCommittedRef.current).substring(0,100),section:currentSection,periodKey:currentPeriodKey},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      }
      // #endregion

      // ✅ SAVE INVARIANT FIX: Declare success outside try block to ensure it's always defined in finally
      let success = false;

      try {
        // ✅ STEP 2.3: Conflict detection - check if data was updated by another user
        if (lastLoadedUpdatedAtRef.current) {
          try {
            // Fetch current updated_at from database to check for conflicts
            const { supabase: supabaseClient } = await import('../lib/supabaseClient');
            const { data: currentRecord } = await supabaseClient
              .from('snapshots')
              .select('updated_at')
              .eq('workspace_id', workspaceId!)
              .eq('week_key', currentPeriodKey)
              .eq('section_key', currentSection)
              .maybeSingle();
            
            if (currentRecord && typeof currentRecord === 'object' && 'updated_at' in currentRecord) {
              const record = currentRecord as { updated_at: string };
              const currentUpdatedAt = record.updated_at;
              const lastLoadedUpdatedAt = lastLoadedUpdatedAtRef.current;
              
              // If database updated_at is newer than when we loaded, conflict detected
              if (currentUpdatedAt > lastLoadedUpdatedAt) {
                // ✅ FIX 1: BLOCK save on conflict - do not proceed with save
                showErrorToast(
                  'Conflict Detected',
                  'This data was updated elsewhere. Reload to continue.',
                  10000,
                  {
                    label: 'Reload',
                    onClick: () => {
                      window.location.reload();
                    }
                  }
                );
                
                if (import.meta.env.DEV) {
                  logger.warn('Conflict detected - save blocked', {
                    section: currentSection,
                    periodKey: currentPeriodKey,
                    lastLoaded: lastLoadedUpdatedAt,
                    current: currentUpdatedAt
                  }, `useSimpleSnapshot:${currentSection}`);
                }
                
                // Early return - DO NOT save (still emit save-complete so indicator shows)
                emitSaveEndIfUserInitiated();
                return;
              }
            }
          } catch (conflictCheckError) {
            // Ignore conflict check errors - proceed with save
            if (import.meta.env.DEV) {
              logger.debug('Conflict check failed (non-critical)', { 
                error: conflictCheckError 
              }, `useSimpleSnapshot:${currentSection}`);
            }
          }
        }
        
        // ✅ USER REQUEST: Remove "Saving..." indicator - only show "Saved at [time]" after save completes
        // Don't call notifySaveStart() - user wants to skip the "Saving..." state
        // We'll only call notifySaveEnd() after successful save to show "Saved at [time]"
        setIsSaving(true);
        
        // Create save promise and track it
        // ✅ CRITICAL FIX: Save the LATEST data, not stale closure value
        // ✅ FIX: Wrap in try-catch to prevent unhandled promise rejections
        const savePromise = saveSnapshot(dataPeriodKey, dataSection, dataToSave, workspaceId!)
          .catch((error) => {
            // Log error but don't throw - return false to indicate save failed
            logger.error('Save failed', { 
              error, 
              section: currentSection, 
              periodKey: currentPeriodKey,
              errorMessage: error instanceof Error ? error.message : String(error)
            }, `useSimpleSnapshot:${currentSection}`);
            return false; // Return false instead of throwing
          })
          .finally(() => {
            // Always remove from pending saves after completion (success or error)
            pendingSaves.current.delete(saveKey);
          });
        pendingSaves.current.set(saveKey, savePromise);
        
        // ✅ SAVE INVARIANT FIX: Primary save success is determined ONLY by the Supabase upsert
        // Secondary operations (relinkFutureWeeksOnModification) are fire-and-forget and never affect this
        success = await savePromise;
        
        // 🔍 PRODUCTION DEBUG: Log save result (ALL sections)
        console.log(`[useSimpleSnapshot] ${currentSection} save completed`, {
          success,
          periodKey: currentPeriodKey,
          section: currentSection,
          dataPreview: JSON.stringify(dataToSave).substring(0, 100),
          timestamp: new Date().toISOString()
        });
        // #region agent log (dev only — avoids CSP connect-src violations in production)
        if (import.meta.env.DEV) {
          fetch('http://127.0.0.1:7242/ingest/9f1fa8c7-8ce8-473c-bb6b-bdbbaff33d50',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useSimpleSnapshot.ts:2541',message:'savePromise result',data:{success,section:currentSection,periodKey:currentPeriodKey},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
        }
        // #endregion
        
        // ✅ STEP 2.3: Update lastLoadedUpdatedAtRef after successful save
        if (success) {
          try {
            // Fetch updated updated_at from database
            const { supabase: supabaseClient } = await import('../lib/supabaseClient');
            const { data: savedRecord } = await supabaseClient
              .from('snapshots')
              .select('updated_at')
              .eq('workspace_id', workspaceId!)
              .eq('week_key', currentPeriodKey)
              .eq('section_key', currentSection)
              .maybeSingle();
            
            if (savedRecord && typeof savedRecord === 'object' && 'updated_at' in savedRecord) {
              const record = savedRecord as { updated_at: string };
              lastLoadedUpdatedAtRef.current = record.updated_at;
            }
          } catch (updateError) {
            // Ignore update errors - non-critical
            if (import.meta.env.DEV) {
              logger.debug('Failed to update lastLoadedUpdatedAtRef', { 
                error: updateError 
              }, `useSimpleSnapshot:${currentSection}`);
            }
          }
        }
        
        // ✅ FIX: Double-check sequence before updating state
        if (!isMountedRef.current || currentSequence !== saveSequenceRef.current) {
          return; // Stale save, don't update state
        }
        
        if (success) {
          // ✅ PERSISTENCE: Sync React state so notesContent reflects committed value; prevents hydration overwrite
          setState({ data: dataToSave as T, isLoading: false });
          // ✅ CRITICAL FIX: Use LATEST data, not stale closure value
          lastCommittedRef.current = dataToSave;
          // Note: didUserInitiateSaveRef is cleared in finally block after checking it
          
          // ✅ CRITICAL FIX: Clear pending save data ref after successful save
          // Only clear if this was the latest save (sequence matches)
          if (latestPendingSave.sequence === saveSequenceRef.current) {
            pendingSaveDataRef.current = null;
          }
          
          // ✅ STEP 2.1: Clear failed save ref on successful save
          if (failedSaveRef.current) {
            failedSaveRef.current = null;
          }
          
          // ✅ BUG 1 FIX: Funnel emptiness is determined by structure, NOT by numeric values
          // For Funnel, if it's an object, it's never empty (including all zeros)
          // Only clear cache for truly empty data (empty arrays, empty notes)
          const isEmpty = dataSection === 'funnel' ? false : (
            Array.isArray(dataToSave) && dataToSave.length === 0 ||
            (dataSection === 'notes' && typeof dataToSave === 'object' && dataToSave !== null &&
             (dataToSave as any).type === 'doc' && 
             Array.isArray((dataToSave as any).content) &&
             (dataToSave as any).content.length === 1 &&
             (dataToSave as any).content[0]?.type === 'paragraph' &&
             (!(dataToSave as any).content[0]?.content || (dataToSave as any).content[0].content.length === 0))
          );
          
          // ✅ UNIFORM CACHE HANDLING: Treat ALL sections the same way
          // - Empty data → clear cache
          // - Non-empty data → update cache with new data
          // This ensures consistency and prevents data loss/resurrection bugs
          if (isEmpty) {
            // Clear cache for truly empty data (empty arrays, empty TipTap doc)
            console.log(`[useSimpleSnapshot] ${dataSection} data is empty - clearing cache`, {
              periodKey: dataPeriodKey,
              section: dataSection
            });
            try {
              Cache.clear(periodKey, section);
              // #region agent log (dev only — avoids CSP connect-src violations in production)
              if (import.meta.env.DEV) {
                fetch('http://127.0.0.1:7242/ingest/9f1fa8c7-8ce8-473c-bb6b-bdbbaff33d50',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useSimpleSnapshot.ts:2622',message:'Cache cleared for empty data',data:{isEmpty,section:currentSection,periodKey:currentPeriodKey},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
              }
              // #endregion
              if (import.meta.env.DEV) {
                logger.debug('Cleared cache for empty data', { section, periodKey }, `useSimpleSnapshot:${section}`);
              }
            } catch (error) {
              // Silently fail - cache clear shouldn't block save
            }
          } else {
            // ✅ CONFIRMED-WRITE CACHE: ALL sections update cache AFTER successful save
            // For cumulative sections: This is the ONLY cache write (no optimistic)
            // For Notes: This confirms the optimistic write (redundant but safe)
            console.log(`[useSimpleSnapshot] ${dataSection} confirmed cache write (post-save)`, {
              periodKey: dataPeriodKey,
              section: dataSection,
              dataPreview: JSON.stringify(dataToSave).substring(0, 100)
            });
            try {
              Cache.set(dataPeriodKey, dataSection, dataToSave);
              // #region agent log (dev only — avoids CSP connect-src violations in production)
              if (import.meta.env.DEV) {
                fetch('http://127.0.0.1:7242/ingest/9f1fa8c7-8ce8-473c-bb6b-bdbbaff33d50',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useSimpleSnapshot.ts:2634',message:'Cache updated with saved data',data:{isEmpty,section:currentSection,periodKey:currentPeriodKey},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
              }
              // #endregion
              if (import.meta.env.DEV) {
                logger.debug('Updated cache with saved data', { section, periodKey }, `useSimpleSnapshot:${section}`);
              }
            } catch (error) {
              // Silently fail - cache update shouldn't block save
              if (import.meta.env.DEV) {
                logger.debug('Cache update failed after save', { error, section, periodKey }, `useSimpleSnapshot:${section}`);
              }
            }
          }
          
          // ✅ CRITICAL FIX: Clear cache when deletions occur for ALL cumulative sections
          // This ensures deleted/hidden items don't persist in future weeks via stale cache
          // Live Assets use hidden: true, Campaign Spend & Promo Codes use deleted: true
          // ✅ CRITICAL FIX: Use LATEST data, not stale closure value
          if (success && (dataSection === 'campaignSpend' || dataSection === 'promoCodes' || dataSection === 'liveAssets')) {
            const hasDeletions = Array.isArray(dataToSave) && dataToSave.some((item: unknown) => {
              const itemObj = item as Record<string, unknown>;
              return itemObj.deleted || itemObj.hidden;  // Check both deleted and hidden flags
            });
            
            if (hasDeletions) {
              try {
                // Clear cache for this week to force fresh load on next access
                // This prevents deleted/hidden items from being forward-filled via stale cache
                // ✅ CRITICAL FIX: Use LATEST data period/section, not stale closure values
                Cache.clear(dataPeriodKey, dataSection);
                if (import.meta.env.DEV) {
                  const deletedCount = (next as Array<Record<string, unknown>>).filter((item) => item.deleted || item.hidden).length;
                  logger.debug('Cleared cache due to deletions', {
                    section,
                    periodKey,
                    deletedCount,
                    deletedItems: (next as Array<Record<string, unknown>>).filter((item) => item.deleted).length,
                    hiddenItems: (next as Array<Record<string, unknown>>).filter((item) => item.hidden).length
                  }, `useSimpleSnapshot:${section}`);
                }
              } catch (error) {
                // Silently fail - cache clear shouldn't block save
              }
            }
          }
          
          // ✅ NEW: Propagate forward-fill updates if admin and data is meaningful
          // Only propagate if this is a past/current week (not future) and data is meaningful
          // Viewers cannot trigger forward-fill propagation
          // ✅ FIX 3: Use canEditSection instead of isAdmin for permission check
          // ✅ CRITICAL FIX: Use LATEST data section, not stale closure value
          if (!canEditSection(dataSection)) {
            // Viewers cannot trigger forward-fill propagation
            if (import.meta.env.DEV) {
              logger.debug('Skipping forward-fill propagation (viewer save)', {
                section,
                periodKey,
                userId
              }, `useSimpleSnapshot:${section}`);
            }
          } else {
            // ✅ CRITICAL FIX: Use LATEST data, not stale closure value
            const hasMeaningful = hasMeaningfulData(dataToSave, dataSection);
            
            // CRITICAL FIX: Don't propagate empty data to future weeks
            // Empty arrays and all-zero funnel data should NOT trigger RPC propagation
            let shouldPropagate = hasMeaningful;
            
            // Check 1: Empty arrays should not propagate (user explicitly cleared data)
            if (Array.isArray(next) && next.length === 0) {
              shouldPropagate = false;
              if (import.meta.env.DEV) {
                logger.debug('Skipping RPC propagation - empty array', {
                  section,
                  periodKey
                }, `useSimpleSnapshot:${section}`);
              }
            }
            
            // ✅ BUG 1 FIX: Funnel propagation is based on hasMeaningfulData (structure check)
            // No special handling needed - hasMeaningfulData already handles Funnel correctly
            // (returns true if it's an object, regardless of values)
            
            const currentDate = getWeekStartDateFromKey(getCurrentWeekKey());
            const targetDate = getWeekStartDateFromKey(periodKey);
            const isPastOrCurrentWeek = targetDate && currentDate && targetDate.getTime() <= currentDate.getTime();
            const willCallRPC = !!(userId && shouldPropagate && isPastOrCurrentWeek);

            // Log diagnostic info to database
            logForwardFillDiagnostic(section, periodKey, {
              isAdmin,
              userId,
              hasMeaningfulData: hasMeaningful,
              isPastOrCurrentWeek: !!isPastOrCurrentWeek,
              currentWeek: getCurrentWeekKey(),
              willCallRPC
            });

            if (willCallRPC) {
              // ✅ CRITICAL FIX: Prevent duplicate RPC calls for the same section/week
              // This prevents race conditions when rapid saves occur
              const rpcKey = `${section}:${periodKey}`;
              let rpcPromise = pendingRpcCalls.current.get(rpcKey);
              
              if (!rpcPromise) {
                // Create new RPC call promise
                rpcPromise = propagateForwardFillUpdate(section, periodKey, next, workspaceId!)
                  .finally(() => {
                    // Remove from pending map when done (success or failure)
                    pendingRpcCalls.current.delete(rpcKey);
                  });
                
                pendingRpcCalls.current.set(rpcKey, rpcPromise);
                
                if (import.meta.env.DEV) {
                  logger.debug('Started new RPC call', {
                    section,
                    periodKey,
                    rpcKey
                  }, `useSimpleSnapshot:${section}`);
                }
              } else {
                // RPC call already in progress for this section/week - reuse existing promise
                if (import.meta.env.DEV) {
                  logger.debug('Reusing existing RPC call (prevent duplicate)', {
                    section,
                    periodKey,
                    rpcKey
                  }, `useSimpleSnapshot:${section}`);
                }
              }
              
              // Handle RPC result (whether new or reused)
              rpcPromise
                .then((updatedRows) => {
                  // Log success
                  logForwardFillDiagnostic(section, periodKey, {
                    isAdmin,
                    userId,
                    hasMeaningfulData: hasMeaningful,
                    isPastOrCurrentWeek: !!isPastOrCurrentWeek,
                    currentWeek: getCurrentWeekKey(),
                    willCallRPC: true
                  }, { updatedRows });
                  
                  if (updatedRows > 0) {
                    // ✅ Cache invalidation after RPC propagation
                    // Clear cache for this section to ensure fresh data on next load
                    // Note: This improves UX by showing updated data immediately
                    // The cache has 5-min TTL, but clearing ensures consistency
                    try {
                      Cache.clearSection(section);
                      if (import.meta.env.DEV) {
                        logger.debug('Cache cleared after RPC propagation', {
                          section,
                          updatedRows
                        }, `useSimpleSnapshot:${section}`);
                      }
                    } catch (error) {
                      // Silently fail - cache invalidation shouldn't block
                      if (import.meta.env.DEV) {
                        logger.debug('Cache clear failed after RPC', { error, section });
                      }
                    }
                    
                    if (import.meta.env.DEV) {
                      logger.debug('Forward-fill propagated after save', {
                        section,
                        sourceWeek: periodKey,
                        updatedRows
                      }, `useSimpleSnapshot:${section}`);
                    } else {
                      logger.info('[Forward-Fill] Propagated after save', {
                        section,
                        sourceWeek: periodKey,
                        updatedRows
                      });
                    }
                  }
                })
                .catch((error) => {
                  // ✅ Handle RPC failures gracefully - don't block save
                  logger.error('Forward-fill propagation failed', { 
                    error, 
                    section, 
                    periodKey 
                  }, `useSimpleSnapshot:${section}`);
                  
                  // Log diagnostic entry
                  logForwardFillDiagnostic(section, periodKey, {
                    isAdmin,
                    userId,
                    hasMeaningfulData: hasMeaningful,
                    isPastOrCurrentWeek: !!isPastOrCurrentWeek,
                    currentWeek: getCurrentWeekKey(),
                    willCallRPC: true
                  }, { updatedRows: 0, error: error?.message || String(error) });
                  
                  // Don't rethrow - save already succeeded, propagation is best-effort
                });
            }
          }
        } else {
        }
      } catch (error) {
        // Ensure pending save is removed even on error
        pendingSaves.current.delete(saveKey);
        
        // Only handle error if this is still the current save
        if (isMountedRef.current && currentSequence === saveSequenceRef.current) {
          logger.error('Save failed', { error, section: currentSection, periodKey: currentPeriodKey }, `useSimpleSnapshot:${currentSection}`);
          
          // ✅ STEP 2.1: Show error toast with retry button
          const errorClassification = classifyError(error);
          
          // ✅ FIX 2: Store current state reference for retry (not stale data)
          // Retry will save current state, not the failed state
          // Use defaultValue if dataRef.current is undefined
          failedSaveRef.current = {
            data: dataRef.current ?? defaultValue, // Current state from ref, not next (which failed)
            periodKey: currentPeriodKey,
            section: currentSection
          };
          
          // Create retry function that saves CURRENT state (not stale failed data)
          const retryFn = async () => {
            if (failedSaveRef.current && 
                failedSaveRef.current.periodKey === currentPeriodKey && 
                failedSaveRef.current.section === currentSection) {
              // ✅ FIX 2: Always use current state from ref, not stale failed data
              const currentState = dataRef.current ?? defaultValue;
              
              failedSaveRef.current = null; // Clear failure tracking
              try {
                await save(currentState);
              } catch (retryError) {
                // Error handling is done in save() function
                logger.error('Retry save failed', { error: retryError, section: currentSection, periodKey: currentPeriodKey }, `useSimpleSnapshot:${currentSection}`);
              }
            }
          };
          
          // Show error toast with retry action (only if retryable)
          if (errorClassification.isRetryable) {
            showErrorToast(
              'Save Failed',
              errorClassification.userMessage,
              10000, // Longer duration for error toasts
              {
                label: 'Retry',
                onClick: retryFn
              }
            );
          } else {
            // Non-retryable errors (auth, permission) - no retry button
            showErrorToast(
              'Save Failed',
              errorClassification.userMessage,
              8000
            );
          }
        }
      } finally {
        // Only update saving state if this is still the current save
        if (isMountedRef.current && currentSequence === saveSequenceRef.current) {
          setIsSaving(false);
          // ✅ BUG 2 FIX: Save indicator based on user-initiated save, NOT value equality
          // The indicator shows when primary save succeeds AND it was user-initiated
          // This avoids false negatives from deep equality comparisons (especially for all-zero Funnel)
          const wasUserInitiated = didUserInitiateSaveRef.current;
          // ✅ ARCHITECTURAL: Show save indicator for ALL sections on user-initiated edits
          // Unified behavior: if user initiated the save, show indicator (even if skipped due to equality)
          const shouldShowIndicator = wasUserInitiated;
          if (shouldShowIndicator) {
            // Clear the flag after using it
            didUserInitiateSaveRef.current = false;
            setTimeout(() => {
              // Double-check we're still mounted and this is still the latest save
              // #region agent log (dev only — avoids CSP connect-src violations in production)
              if (import.meta.env.DEV) {
                fetch('http://127.0.0.1:7242/ingest/9f1fa8c7-8ce8-473c-bb6b-bdbbaff33d50',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useSimpleSnapshot.ts:2951',message:'setTimeout callback for notifySaveEnd',data:{isMounted:isMountedRef.current,currentSequence,saveSequence:saveSequenceRef.current,matches:isMountedRef.current && currentSequence === saveSequenceRef.current,section:currentSection,periodKey:currentPeriodKey},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'G'})}).catch(()=>{});
              }
              // #endregion
              if (isMountedRef.current && currentSequence === saveSequenceRef.current) {
                // #region agent log (dev only — avoids CSP connect-src violations in production)
                if (import.meta.env.DEV) {
                  fetch('http://127.0.0.1:7242/ingest/9f1fa8c7-8ce8-473c-bb6b-bdbbaff33d50',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useSimpleSnapshot.ts:2956',message:'Calling notifySaveEnd',data:{section:currentSection,periodKey:currentPeriodKey},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'G'})}).catch(()=>{});
                }
                // #endregion
                notifySaveEnd();
              }
            }, 100);
          }
        }
      }
    }, debounceMs);
  }, [workspaceReady, workspaceId, section, periodKey, notifySaveStart, notifySaveEnd, canEditSection, userId, defaultValue]);
  // ✅ REMOVED state.data from deps - prevents re-creation on every keystroke
  // Data is accessed via closures over the 'next' parameter, not from state

  // Flush function for immediate save (used on navigation/tab switching)
  // ✅ ARCHITECTURAL FIX: Stable handler with NO unstable deps (state.data removed)
  // Uses refs only for data access to prevent handler thrashing
  // This ensures the registered handler always has access to latest data
  const flushSave = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    // ✅ CRITICAL: Access data via refs only (no state.data dependency)
    // pendingSaveDataRef has latest data from rapid typing
    // dataRef has latest committed state
    // This prevents handler re-creation on every keystroke
    const latestPendingSave = pendingSaveDataRef.current;
    const pendingData = latestPendingSave?.data ?? dataRef.current;
    
    // ✅ FIX: Actually trigger the save if there's pending data
    // Check if pending data differs from lastCommittedRef (meaning there's unsaved data)
    if (pendingData !== undefined && !isEqual(pendingData, lastCommittedRef.current)) {
      const currentSequence = ++saveSequenceRef.current;
      const currentPeriodKey = periodKey;
      const currentSection = section;
      // ✅ CRITICAL: Optimistic cache ONLY for Notes (Google Docs-like)
      // Cumulative sections wait for confirmed save to update cache
      if (currentSection === 'notes') {
        try {
          Cache.set(currentPeriodKey, currentSection, pendingData);
        } catch {
          // ignore cache errors (non-critical)
        }
      }
      // Only save if component is still mounted and period/section haven't changed
      if (isMountedRef.current && currentPeriodKey === periodKey && currentSection === section) {
        try {
          // ✅ FIX: Flush saves are background operations (tab switching, unmount, etc.)
          // Don't show save indicator - these are not user-initiated saves
          // Trigger immediate save (bypass debounce) silently
          const saveKey = `${workspaceId}:${currentPeriodKey}:${currentSection}`;
          
          // Check if there's already a pending save
          const existingSave = pendingSaves.current.get(saveKey);
          if (existingSave) {
            // Wait for existing save to complete
            try {
              await existingSave;
            } catch (error) {
              // Ignore errors from existing save
            }
            // Check again after waiting
            if (isEqual(dataRef.current, lastCommittedRef.current)) {
              return; // Already saved
            }
          }
          
          // Save silently without showing indicator
          // ✅ FIX: Add error handling to prevent unhandled promise rejections
          const savePromise = saveSnapshot(currentPeriodKey, currentSection, pendingData, workspaceId!)
            .catch((error) => {
              // Log error but don't throw - return false to indicate save failed
              logger.error('Flush save failed', { 
                error, 
                section: currentSection, 
                periodKey: currentPeriodKey,
                errorMessage: error instanceof Error ? error.message : String(error)
              }, `useSimpleSnapshot:${currentSection}`);
              return false; // Return false instead of throwing
            })
            .finally(() => {
              pendingSaves.current.delete(saveKey);
            });
          pendingSaves.current.set(saveKey, savePromise);
          
          const success = await savePromise;
          if (success && isMountedRef.current && currentSequence === saveSequenceRef.current) {
            setState({ data: pendingData as T, isLoading: false });
            lastCommittedRef.current = pendingData;
            // ✅ CONFIRMED-WRITE CACHE: ALL sections update cache AFTER successful save
            // This is the authoritative cache update for cumulative sections (LiveAssets, CampaignSpend, etc.)
            // For Notes, this is a redundant confirmation (already optimistically cached)
            try {
              Cache.set(currentPeriodKey, currentSection, pendingData);
            } catch {
              // ignore cache errors (non-critical)
            }
            // ✅ CRITICAL FIX: Clear pendingSaveDataRef after successful flush save
            // This prevents stale data from being used in future saves
            if (latestPendingSave && latestPendingSave.sequence === currentSequence) {
              pendingSaveDataRef.current = null;
            }
          }
        } catch (error) {
          logger.error('Flush save failed', { error, section: currentSection, periodKey: currentPeriodKey }, `useSimpleSnapshot:${currentSection}`);
        }
      }
    }
  }, [periodKey, section]); // ✅ STABLE: Only primitives that change on context switch

  // ✅ ARCHITECTURAL: Auto-register flush handler for ALL sections
  // This ensures flushAll() can flush all sections on context changes (tab, week, unmount)
  // IMPORTANT: Must be AFTER flushSave definition to avoid TDZ errors in production builds
  useEffect(() => {
    if (!workspaceReady || !snapshotFlush) return;

    // Register this section's flush handler
    snapshotFlush.registerFlush(section, periodKey, flushSave);

    return () => {
      // Unregister on unmount or when periodKey changes
      snapshotFlush.unregisterFlush(section, periodKey);
    };
  }, [workspaceReady, section, periodKey, snapshotFlush, flushSave]);

  // flushSaveNow: await save before navigation. No isMountedRef; updates cache on success.
  // Navigation (tab/week) must await this so DB write completes before unmount.
  // Safeguard: do not overwrite meaningful committed data with empty/default (prevents data loss).
  const flushSaveNow = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    const pending = pendingSaveDataRef.current;
    const ws = workspaceIdRef.current;
    if (!pending || !ws) return;
    if (isEqual(pending.data, lastCommittedRef.current)) return;
    const committed = lastCommittedRef.current;
    if (committed !== undefined && hasMeaningfulData(committed, pending.section) && !hasMeaningfulData(pending.data, pending.section)) {
      return; // don't overwrite real data with empty
    }
    try {
      const success = await saveSnapshot(pending.periodKey, pending.section, pending.data, ws);
      if (success) {
        setState({ data: pending.data as T, isLoading: false });
        lastCommittedRef.current = pending.data;
        pendingSaveDataRef.current = null;
        // ✅ CONFIRMED-WRITE CACHE: ALL sections (post-save, after success)
        try {
          Cache.set(pending.periodKey, pending.section, pending.data);
        } catch {
          // ignore cache errors
        }
      }
    } catch (error) {
      logger.error('flushSaveNow failed', { error, section: pending.section, periodKey: pending.periodKey }, `useSimpleSnapshot:${pending.section}`);
    }
  }, []);

  // ✅ STEP 2.1: Retry function for failed saves
  const retryFailedSave = useCallback(async () => {
    if (!failedSaveRef.current) {
      return;
    }
    
    const { periodKey: failedPeriodKey, section: failedSection } = failedSaveRef.current;
    
    // Only retry if still on the same week/section
    if (failedPeriodKey !== periodKey || failedSection !== section) {
      failedSaveRef.current = null; // Clear stale failure
      return;
    }
    
    // ✅ FIX 2: Always save CURRENT state from ref, not stale failed data
    const currentState = dataRef.current ?? defaultValue;
    
    // Clear failure tracking
    failedSaveRef.current = null;
    
    // Retry the save with current state
    try {
      await save(currentState);
    } catch (error) {
      // Error handling is done in save() function
      logger.error('Retry save failed', { error, section, periodKey }, `useSimpleSnapshot:${section}`);
    }
  }, [save, periodKey, section, defaultValue]);
  // ✅ REMOVED state.data from deps - prevents re-creation on every keystroke

  return { 
    ...state,
    save,
    flushSave,
    flushSaveNow,
    retryFailedSave,
    hasFailedSave: failedSaveRef.current !== null,
    isLoading: state.isLoading,
    isSaving,
    rootKey,
    workspaceReady,
    loadError // ✅ RELIABILITY: Expose load error for components to show retry UI
  };
}
